/* eslint-disable no-unused-vars */
const THREE = window.THREE;
import store from '@/store';
import { toPoint2D } from '../../utils/threejsUtils';
import { getURNThumbnail, getNodeSortedProperties } from '../../utils/forgeUtils';
import {
  convertLatLongToXYZCoordinates_Indian,
  getRotatedDirectionByNorthAngle,
  bytesToSize,
  ForgeVisiblityMode,
} from './forgeService';
import { formatDate } from '@/utils';
import { ModelsStatus } from '@/components/views/smallModels/config/enum';
import { AnomalyCondition, TAG_CONDITION_NONE } from '@/features/Share/config/enums';
import { CORROSION_CALCULATION, getCategoryByMaxDistance } from '@/features/Dashboard/config/enums';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { VIEWER_BACKGROUND_DEFAULT } from '@/utils/viewerBackground';
import { isScaffoldMarkerPlaced } from '@/features/Tags/utils/tagMarkerLifecycle';

export const TAG_COLOR = {
  Equipment: 0x00a8b7,
  Pipeline: 0x547d9a,
  Structural: 0x5f6f64,
  General: 0x665b9b,
  Scaffold: 0x665b9b,
  Permit: 0x665b9b,
  Container: 0x665b9b,
  Inspection: 0x9a5454,
  Anomaly: 0xFF6347,
  AvaInspect: 0x61b695,
  CmlRecord: 0xdf620a,
}

export const TAG_COLOR_HEX = {
  Equipment: '#00a8b7',
  Pipeline: '#547d9a',
  Structural: '#5f6f64',
  General: '#665b9b',
  Scaffold: '#665b9b',
  Permit: '#665b9b',
  Container: '#665b9b',
  Inspection: '#9a5454',
  Anomaly: '#FF6347',
  AvaInspect: '#61b695',
  CmlRecord: '#df620a',
}

export class ForgeObject {
  constructor() {
    this.id;
    this.dbId;
    this.center;
    this.urn = '';
    this.min;
    this.max;
    this.property;
    this.value;

    this.name = '';
  }
  clone() {
    var obj = new ForgeObject();

    obj.id = this.id;
    obj.dbId = this.dbId;
    obj.urn = this.urn;
    obj.name = this.name;
    obj.property = this.property;
    obj.value = this.value;
    obj.min = this.min;
    obj.max = this.max;

    if (this.center) {
      if (!(this.center instanceof THREE.Vector3)) {
        this.center = new THREE.Vector3(this.center.x, this.center.y, this.center.z);
      }
      obj.center = this.center.clone();
    }
    return obj;
  }
}
export class Linked360ImageInfo {
  constructor() {
    this.imageId = null;
  }
}

export class LinkedScanInfo {
  constructor() {
    this.pointCloudId = null;
  }
}

function cloneSpatialTargets(spatialTargets) {
  return Array.isArray(spatialTargets)
    ? spatialTargets.map((target) => ({ ...target }))
    : [];
}

function copySpatialTargets(target, source) {
  if (Object.prototype.hasOwnProperty.call(source || {}, 'spatialTargets')) {
    target.spatialTargets = cloneSpatialTargets(source.spatialTargets);
  } else {
    delete target.spatialTargets;
  }
}

export class ForgeBaseTag {
  constructor() {
    this.id;
    this.name = '';
    this.isMaster = false;
    this.activityId = '';
    this.workPackNumber = '';
    this.workPackTitle = '';
    this.createDate = null;
    this.createdBy = '';
    this.thumbnailUrl = '';
    this.attachments = [];
    this.documents = [];
    this.comments = [];
    this.linkedTags1 = [];
    this.linkedTags2 = [];
    this.linked360ImageInfo = null;
    this.area2dCoordinates = null;
    this.level2D = null;
    this.forgeAssetId = store.state.assetId;
    this.createdById = null;
    this.tasks = [];
    this.tasksTree = null;
    this.system = '';
    this.subSystem = '';
    this.documentNo = '';
    this.plant = '';
    this.area = '';
    this.unit = '';
    this.funcLocation = '';
    this.insulationCode = '';
    // Never null: the API binds `Condition` as a non-nullable enum, so a null
    // 400s every tag write (AVA-492). 0 is the server's `Condition.None`.
    this.condition = TAG_CONDITION_NONE;
    this.groupId = null;
    this.cropVolumeJson = null; // Serialized ForgeCropBox

    // Custom attributes support (from MasterImport)
    this.customAttributesJson = null;  // JSON string of custom attributes
    this.masterImportBatchId = null;   // FK to batch for schema lookup

    // FacilityGroup support properties
    this.availableAssets = [];           // All facilities where tag is harvested
    this.otherAvailableAssets = [];      // Other facilities (excluding current)
    this.isHarvestedInCurrentFacility = false;  // Quick check flag
    this.hasCurrentFacilityHarvest = null; // Nullable server-scoped harvesting ownership

    // Per-facility point override (resolved from TagFacilityOverride via ApplyOverrides)
    this.positionImage360Id = null;      // Image360 scan ID — non-null means point is from 360 scan

    // Cross-facility position data (parallels availableAssets pattern)
    this.positionedFacilityIds = [];         // All facilities where tag has 360-scan position
    this.otherPositionedFacilityIds = [];    // Other facilities (excluding current) with position
  }

  /**
   * Processes children for FacilityGroup support.
   * Extracts available assets metadata, then filters children to current facility.
   * Must be called AFTER children are converted to ForgeObject instances.
   */
  processChildrenForFacilityGroup() {
    if (!this.children || !this.children.length) {
      this.availableAssets = [];
      this.otherAvailableAssets = [];
      this.isHarvestedInCurrentFacility = false;
      this.otherPositionedFacilityIds = (this.positionedFacilityIds || [])
        .filter(id => id !== store.state.assetId);
      return;
    }

    const currentAssetId = store.state.assetId;

    // 1. Extract unique asset IDs BEFORE filtering (excluding null/legacy)
    this.availableAssets = [...new Set(
      this.children
        .map(c => c.forgeAssetId)
        .filter(id => id != null)
    )];

    // 2. Other facilities where this tag exists (for navigation menu)
    this.otherAvailableAssets = this.availableAssets.filter(id => id !== currentAssetId);

    // 3. Check if harvested in current facility
    this.isHarvestedInCurrentFacility = this.children.some(
      c => c.forgeAssetId === currentAssetId || c.forgeAssetId == null
    );

    // 4. Filter children to current facility only (keeps existing code working)
    this.children = this.children.filter(
      c => c.forgeAssetId === currentAssetId || c.forgeAssetId == null
    );

    // 5. Derive cross-facility position data (parallels availableAssets)
    this.otherPositionedFacilityIds = (this.positionedFacilityIds || [])
      .filter(id => id !== currentAssetId);
  }

  get group() {
    let groups = store.getters['TAGS_GROUPS/GET_TAG_GROUPS'] ?? [];

    if (!groups?.length) return '';

    return groups.find((g) => g.id == this.groupId)?.name ?? '';
  }

  /**
   * Parse and return custom attributes as an object.
   * Returns empty object if no custom attributes or parse error.
   */
  get customAttributes() {
    if (!this.customAttributesJson) return {};
    try {
      return typeof this.customAttributesJson === 'string'
        ? JSON.parse(this.customAttributesJson)
        : this.customAttributesJson;
    } catch (e) {
      debugWarn('[ForgeBaseTag] Failed to parse customAttributesJson:', e);
      return {};
    }
  }

  /**
   * Set custom attributes (serializes to JSON).
   */
  set customAttributes(value) {
    this.customAttributesJson = value && Object.keys(value).length > 0
      ? JSON.stringify(value)
      : null;
  }

  get cropBox() {
    if (!this.cropVolumeJson) return null;

    try {
      const jsonData = JSON.parse(this.cropVolumeJson);


      const cropVolume = jsonData.cropBox || jsonData;

      if (!cropVolume || !cropVolume.Min || !cropVolume.Max) {
        return null;
      }

      return new THREE.Box3(
        new THREE.Vector3(cropVolume.Min.x, cropVolume.Min.y, cropVolume.Min.z),
        new THREE.Vector3(cropVolume.Max.x, cropVolume.Max.y, cropVolume.Max.z)
      );
    } catch (error) {
      debugError('[ForgeBaseTag] Error parsing cropVolumeJson:', error);
      return null;
    }
  }

  get tagActivities() {
    return store.getters['GET_TAGS_ACTIVITIES'].byTagId[this.id] ?? [];
  }

  get tagActivitiesObjects() {
    return (
      store.getters['GET_TAGS_ACTIVITIES'].byTagId[this.id]?.map(
        (o) => store.getters['GET_TAGS_ACTIVITIES'].byActivityNumber[o],
      ) ?? []
    );
  }

  get activitiesString() {
    return this.tagActivities?.join(', ') ?? '';
  }

  get query() {
    let values = this.tagActivitiesObjects.map((t) => t.query).filter((o) => o);

    if (!values?.length) return null;

    return values?.join(', ') ?? '';
  }

  get workPackNumberAM() {
    let values = this.tagActivitiesObjects.map((t) => t.workPackNumber).filter((o) => o);

    if (!values?.length) return null;

    return values?.join(', ') ?? '';
  }
  get _workPackNumber() {
    return this.workPackNumberAM ?? this.workPackNumber;
  }

  get workPackTitleAM() {
    let values = this.tagActivitiesObjects.map((t) => t.workPackTitle).filter((o) => o);

    if (!values?.length) return null;

    return values?.join(', ') ?? '';
  }
  get _workPackTitle() {
    return this.workPackTitleAM ?? this.workPackTitle;
  }

  get linkedTagsList() {
    return this.linkedTags1
      .map((l) => l.secondLinkedTagId)
      .concat(this.linkedTags2.map((l) => l.firstLinkedTagId));
  }

  get area2dPoints() {
    if (!this.area2dCoordinates) return [];
    let numbers = this.area2dCoordinates.split(',').map((o) => parseFloat(o));
    let points = [];
    for (let i = 0; i < numbers.length; i += 3) {
      points.push(new THREE.Vector3(numbers[i], numbers[i + 1], numbers[i + 2]));
    }
    return points;
  }
  set area2dPoints(value) {
    let text = '';
    value.forEach((o) => {
      if (text) text += ',';
      text += `${o.x},${o.y},${o.z}`;
    });
    this.area2dCoordinates = text;
  }
  parseComments() {
    if (!this.comments) this.comments = [];

    this.comments = this.comments.map((o) => Object.assign(new ForgeComment(), o));
  }
  setSameBaseParameters(element) {
    this.id = element.id;
    this.name = element.name;
    this.isMaster = element.isMaster;
    this.activityId = element.activityId;
    this.workPackNumber = element.workPackNumber;
    this.workPackTitle = element.workPackTitle;
    this.createDate = element.createDate;
    this.createdById = element.createdById;
    this.createdBy = element.createdBy;
    this.thumbnailUrl = element.thumbnailUrl;
    this.comments = element.comments ? element.comments.map((o) => o.clone()) : [];
    this.attachments = element.attachments ? element.attachments.map((o) => o.clone()) : [];
    this.documents = element.documents;
    this.linkedTags1 = element.linkedTags1 ? element.linkedTags1.map((o) => o) : [];
    this.linkedTags2 = element.linkedTags2 ? element.linkedTags2.map((o) => o) : [];
    this.level2D = element.level2D;
    this.linked360ImageInfo = element.linked360ImageInfo;
    this.area2dCoordinates = element.area2dCoordinates;
    this.discriminator = element.discriminator;
    this.tasks = element.tasks ? element.tasks.map((o) => o) : [];
    this.tasksTree = element.tasksTree;
    this.system = element.system;
    this.subSystem = element.subSystem;
    this.documentNo = element.documentNo;
    this.plant = element.plant;
    this.area = element.area;
    this.unit = element.unit;
    this.funcLocation = element.funcLocation;
    this.insulationCode = element.insulationCode;
    this.condition = element.condition;
    this.groupId = element.groupId;
    this.cropVolumeJson = element.cropVolumeJson;
    this.customAttributesJson = element.customAttributesJson;
    this.masterImportBatchId = element.masterImportBatchId;

    // FacilityGroup multi-facility fields (AV-516 + AVA-253).
    // These were forgotten in the original commits despite being added to
    // the constructor. Missing these causes .clone() to silently strip
    // multi-facility enrichment — which broke TagCard button logic, the
    // Copy Tag URL panel button, and any future feature that depends on
    // editingTag.availableAssets / otherAvailableAssets / etc.
    // Arrays are shallow-copied to avoid aliasing; facility IDs are strings
    // so shallow copy is sufficient.
    this.availableAssets = element.availableAssets
      ? [...element.availableAssets]
      : [];
    this.otherAvailableAssets = element.otherAvailableAssets
      ? [...element.otherAvailableAssets]
      : [];
    this.isHarvestedInCurrentFacility = !!element.isHarvestedInCurrentFacility;
    this.hasCurrentFacilityHarvest = element.hasCurrentFacilityHarvest ?? null;
    this.facilityGroupId = element.facilityGroupId;
    this.positionedFacilityIds = element.positionedFacilityIds
      ? [...element.positionedFacilityIds]
      : [];
    this.otherPositionedFacilityIds = element.otherPositionedFacilityIds
      ? [...element.otherPositionedFacilityIds]
      : [];
    this.positionImage360Id = element.positionImage360Id;
    copySpatialTargets(this, element);
  }

  static getTitleForProperty(property) {
    const propertyTitles = {
      createdBy: 'Created By',
      activityId: 'Activity Id',
      workPackNumber: 'Work Pack Number',
      workPackTitle: 'Work Pack Title',
      system: 'System',
      subSystem: 'Sub System',
      documentNo: 'Document No',
      plant: 'Plant',
      area: 'Area',
      unit: 'Unit',
      funcLocation: 'Functional Location',
      name: 'Tag No.'
    };

    return propertyTitles[property] || property;
  }

  // shared search properties
  // IMPORANT:: Make sure not to add any object or array without handling its custom validation.
  getSearchProperties() {
    return [
      {
        property: 'name',
        title: 'Name',
      },
      {
        property: 'createdBy',
        title: 'Created By',
      },
      {
        property: 'system',
        title: 'System',
      },
      {
        property: 'subSystem',
        title: 'Sub System',
      },
      {
        property: 'documentNo',
        title: 'Document No.',
      },
      {
        property: 'plant',
        title: 'Plant',
      },
      {
        property: 'area',
        title: 'Area',
      },
      {
        property: 'unit',
        title: 'Unit',
      },
      {
        property: 'funcLocation',
        title: 'Functional Location',
      },
      {
        property: 'query',
        title: 'TQ',
      },
    ];
  }

  createNewLink(tagId) {
    var link = {
      id: '',
      firstLinkedTagId: this.id,
      secondLinkedTagId: tagId,
    };

    this.linkedTags1.push(link);

    return link;
  }

  removeLinkedTag(tagId) {
    this.linkedTags1 = this.linkedTags1.filter((l) => l.secondLinkedTagId != tagId);
    this.linkedTags2 = this.linkedTags2.filter((l) => l.firstLinkedTagId != tagId);
  }

  parseContent() {
    if (Object.prototype.hasOwnProperty.call(this, 'spatialTargets')) {
      this.spatialTargets = cloneSpatialTargets(this.spatialTargets);
    }

    if (this.tasks?.length) {
      let taskTypes = store.getters['TASK_MANAGER/GET_ENTRIES']
      this.tasksTree = []
      this.tasks.forEach(t => {
        t.taskType = taskTypes.find(tt => tt.id == t.typeId)?.name;
        t.name = t.name ?? '__no_name__';
        let group = this.tasksTree.find(g => g.name == t.taskType)
        if (!group) {
          group = { id: t.taskType, name: t.taskType, children: [] }
          this.tasksTree.push(group)
        }
        group.children.push(t);
      });

      this.tasksTree.forEach(g => {
        g.children = g.children.sort((a, b) => a.name?.localeCompare(b.name ?? ''))
      })
      this.tasksTree = this.tasksTree.sort((a, b) => a.name?.localeCompare(b.name ?? ''))
    } else {
      this.tasks = [];
      this.tasksTree = [];
    }
  }

  getTextureType() {
    return 'default';
  }

  /**
   * Gets the nearest 360 image to this tag's position.
   * First checks for a linked 360 image, then falls back to finding the closest image by distance.
   * @returns {Object|null} The nearest 360 image object, or null if no images are available
   */
  getNearest360Image() {
    // Method 1: Check for explicitly linked 360 image first
    if (this.linked360ImageInfo) {
      const linkedImage = store.state.projects
        .flatMap((o) => o.images)
        .find((img) => img.id == this.linked360ImageInfo.imageId);

      if (linkedImage) {
        return linkedImage;
      }
    }

    // Method 2: Fall back to finding closest image by distance
    if (store.state.forgeService) {
      return store.state.forgeService.getClosestImageToPoint(this.getPointInView());
    }

    return null;
  }

  static getTagObject(tag) {
    if (!tag) return null;

    let obj = null;

    switch (tag.discriminator) {
      case 'ForgeEquipment':
        obj = Object.assign(new ForgeEquipment(), tag);
        break;
      case 'ForgeStructural':
        obj = Object.assign(new ForgeStructural(), tag);
        break;
      case 'ForgePipeline':
        obj = Object.assign(new ForgePipeline(), tag);
        break;
      case 'ForgeScaffold':
        obj = Object.assign(new ForgeScaffold(), tag);
        break;
      case 'ForgeAnnotation':
        obj = Object.assign(new ForgeAnnotation(), tag);
        break;
      case 'ForgeInspection':
        obj = Object.assign(new ForgeInspection(), tag);
        break;
      case 'ForgeAnomaly':
        obj = Object.assign(new ForgeAnomaly(), tag);
        break;
      case 'ForgeAvaInspect':
        obj = Object.assign(new ForgeAvaInspect(), tag);
        break;
      default:
        obj = Object.assign(new ForgeBaseTag(), tag);
        break;
    }

    if (obj) {
      obj.parseContent();
    }

    return obj ?? tag;
  }
}

export class ForgeTagShortInfo {
  constructor(type) {
    this.id;
    // this.dbid;
    // this.objectName = '';
    this.children = [];
    this.name = '';
    this.thumbnailUrl = '';
    this.workPackNumber = '';
    this.point = null;
    this.positionImage360Id = null; // from per-facility override
    this.positionedFacilityIds = []; // all facilities with 360-scan position
    this.otherPositionedFacilityIds = []; // excluding current facility (derived)
    this.type = type ?? 'Equipment';

    this.parseContent();
  }

  clone() {
    var element = new ForgeTagShortInfo();
    element.setSameParameters(this);
    return element;
  }

  setSameParameters(element) {
    this.id = element.id;
    // this.dbid = element.dbid;
    // this.objectName = element.objectName;
    this.children = element.children ? element.children.map((o) => o.clone()) : [];
    this.name = element.name;
    this.thumbnailUrl = element.thumbnailUrl;
    // this.workPackNumber = element.workPackNumber;
    this.point = element.point
      ? new THREE.Vector3(element.point.x, element.point.y, element.point.z)
      : null;
    this.positionImage360Id = element.positionImage360Id ?? null;
    this.positionedFacilityIds = element.positionedFacilityIds
      ? [...element.positionedFacilityIds]
      : [];
    // Derive cross-facility positioned facilities (parallels ForgeBaseTag.processChildrenForFacilityGroup)
    const currentAssetId = store.state.assetId;
    this.otherPositionedFacilityIds = this.positionedFacilityIds
      .filter(id => id !== currentAssetId);
    copySpatialTargets(this, element);
  }

  parseContent() {
    if (Object.prototype.hasOwnProperty.call(this, 'spatialTargets')) {
      this.spatialTargets = cloneSpatialTargets(this.spatialTargets);
    }

    if (this.point) this.point = Object.assign(new THREE.Vector3(), this.point);

    if (this.children?.length) {
      this.children = this.children.map((o) => Object.assign(new ForgeObject(), o));
    }

    // Derive cross-facility positioned facilities from API-provided positionedFacilityIds
    // (parallels ForgeBaseTag.processChildrenForFacilityGroup)
    if (this.positionedFacilityIds?.length) {
      const currentAssetId = store.state.assetId;
      this.otherPositionedFacilityIds = this.positionedFacilityIds
        .filter(id => id !== currentAssetId);
    }

    // this.children = [];

    // if (this.dbid) {
    //   let child = new ForgeObject();
    //   child.dbId = this.dbid;
    //   child.center = this.point;
    //   child.name = this.name;

    //   this.children = [child];
    // }
  }

  getAllDbIds() {
    var result = [];
    if (this.children) {
      result = this.children.map((o) => o.dbId);
    }
    return result;
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;

    return store.state.forgeService.worldToView3d(this.point, this.point);
  }

  getGlobalPoint() {
    return this.point;
  }

  getTextureType() {
    switch (this.type) {
      case 'Equipment':
        return 'equipment';
      case 'Pipeline':
        return 'pipeline';
      case 'Structural':
        return 'structural';
      case 'Annotation':
        return 'general';
      case 'Inspection':
        return 'inspection';
      case 'Permit':
        return 'general';
      case 'Container':
        return 'general';
      case 'Scaffold':
        return 'general';
      default:
        return 'default';
    }
  }
}

export class ForgeEquipment extends ForgeBaseTag {
  constructor() {
    super();
    this.cableNo = '';
    this.certificateNo = '';
    this.certifyingAuthority = '';
    this.discipline = '';
    this.equipmentDescription = ''; // Equipment Name / Description
    this.fireZone = '';
    this.lineNo = '';
    this.location = '';
    this.manufacturer = '';
    this.modelNo = '';
    this.parentTag = '';
    this.poNo = '';
    this.processFunction = '';
    this.serialNo = '';
    this.serviceDescription = '';
    this.weight = '';
    this.eqTier1 = '';
    this.eqTier2 = '';
    this.eqTier3 = '';
    this.type = ''; // Category / Type
    this.operationalStatus = '';
    this.dateOfInstallation = '';
    this.technicalSpecification = '';
    this.isPrd = '';
    this.prdOverhaulDate = '';
    this.maintenanceSchedule = '';
    this.warrantyInformation = '';
    this.criticalityRating = '';
    this.spareParts = '';
    this.lno = '';
    this.equipmentClass = '';

    this.category;
    this.p_Id = '';
    this.level2D = null;
    this.tagHarvesterId = null;
    this.tagHarvester = null;
    this.point = null;

    this.isIsolated = false;

    this.children = [];
    this.isSelected = false;
    this.matchedSearch = '';
    this.center;
    this.isExpanded = false;
  }

  getSearchProperties() {
    return [
      {
        property: 'categoryTitle',
        title: 'Category',
      },
      {
        property: 'eqTier1',
        title: 'Eq - Tier 1',
      },
      {
        property: 'eqTier2',
        title: 'Eq - Tier 2',
      },
      {
        property: 'eqTier3',
        title: 'Eq - Tier 3'
      },
      {
        property: 'p_Id',
        title: 'P&ID',
      },
      {
        property: 'cableNo',
        title: 'Cable No.',
      },
      {
        property: 'certificateNo',
        title: 'Certificate No.',
      },
      {
        property: 'certifyingAuthority',
        title: 'Certifying Authority',
      },
      {
        property: 'discipline',
        title: 'Discipline',
      },
      {
        property: 'equipmentDescription',
        title: 'Equipment Description',
      },
      {
        property: 'fireZone',
        title: 'Fire Zone',
      },
      {
        property: 'lineNo',
        title: 'Line No.',
      },
      {
        property: 'location',
        title: 'Location',
      },
      {
        property: 'manufacturer',
        title: 'Manufacturer',
      },
      {
        property: 'modelNo',
        title: 'Model No.',
      },
      {
        property: 'parentTag',
        title: 'Parent Tag',
      },
      {
        property: 'poNo',
        title: 'PO No.',
      },
      {
        property: 'processFunction',
        title: 'Process Function',
      },
      {
        property: 'serialNo',
        title: 'Serial No.',
      },
      {
        property: 'serviceDescription',
        title: 'Service Description',
      },
      {
        property: 'type',
        title: 'Type',
      },
      {
        property: 'operationalStatus',
        title: 'Operational Status',
      },
      {
        property: 'dateOfInstallation',
        title: 'Date of Installation',
      },
      {
        property: 'technicalSpecification',
        title: 'Technical Specification',
      },
      {
        property: 'maintenanceSchedule',
        title: 'Maintenance Schedule',
      },
      {
        property: 'warrantyInformation',
        title: 'Warranty Information',
      },
      {
        property: 'criticalityRating',
        title: 'Criticality Rating',
      },
      {
        property: 'spareParts',
        title: 'Spare Parts',
      },
      {
        property: 'weight',
        title: 'Weight',
      },
      {
        property: 'lno',
        title: 'Pipeline',
      },
      {
        property: 'equipmentClass',
        title: 'Equipment Class',
      },

      ...super.getSearchProperties(),
    ];
  }
  // getTagCenter() {
  //     if (this.center)
  //         return this.center;
  //     if (!this.children || this.children.length == 0 || this.level2D)
  //         return this.point.clone().sub(store.state.viewer.model.getGlobalOffset());
  //     this.center = new THREE.Box3().setFromPoints(this.children.map(o => o.center)).getCenter().sub(store.state.viewer.model.getGlobalOffset());
  //     return this.center;
  // }
  get categoryTitle() {
    let category = store.state.categories.find((c) => c.id == this.category);

    if (category) {
      return category.name;
    }

    return '';
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }
  getGlobalPoint() {
    return this.point;
  }

  getAllDbIds() {
    var result = [];
    if (this.children) {
      result = this.children.map((o) => o.dbId);
    }
    return result;
  }
  getAllTaggedChildren() {
    var list = [this];
    if (this.children == null) return list;
    for (var i = 0; i < this.children.length; i++) {
      var tagged = this.children[i].getAllTaggedChildren();
      list = list.concat(tagged);
    }
    return list.filter((o) => o.children == null);
  }
  getUniqueName(initialName, names) {
    var result = initialName;
    var index = 1;
    while (names.includes(result)) {
      result = initialName + '(' + index + ')';
      index++;
    }
    return result;
  }
  clone() {
    var elem = new ForgeEquipment();
    elem.setSameParameters(this);
    return elem;
  }
  setSameParameters(element) {
    this.setSameBaseParameters(element);
    this.cableNo = element.cableNo;
    this.certificateNo = element.certificateNo;
    this.certifyingAuthority = element.certifyingAuthority;
    this.discipline = element.discipline;
    this.equipmentDescription = element.equipmentDescription;
    this.fireZone = element.fireZone;
    this.lineNo = element.lineNo;
    this.location = element.location;
    this.manufacturer = element.manufacturer;
    this.modelNo = element.modelNo;
    this.parentTag = element.parentTag;
    this.poNo = element.poNo;
    this.processFunction = element.processFunction;
    this.serialNo = element.serialNo;
    this.serviceDescription = element.serviceDescription;
    this.type = element.type;
    this.operationalStatus = element.operationalStatus;
    this.dateOfInstallation = element.dateOfInstallation;
    this.technicalSpecification = element.technicalSpecification;
    this.isPrd = element.isPrd;
    this.prdOverhaulDate = element.prdOverhaulDate;
    this.maintenanceSchedule = element.maintenanceSchedule;
    this.warrantyInformation = element.warrantyInformation;
    this.criticalityRating = element.criticalityRating;
    this.spareParts = element.spareParts;
    this.weight = element.weight;
    this.category = element.category;
    this.eqTier1 = element.eqTier1;
    this.eqTier2 = element.eqTier2;
    this.eqTier3 = element.eqTier3;
    this.lno = element.lno;
    this.p_Id = element.p_Id;
    this.level2D = element.level2D;
    this.tagHarvesterId = element.tagHarvesterId;
    this.tagHarvester = element.tagHarvester;
    this.isIsolated = element.isIsolated;
    this.point = element.point
      ? new THREE.Vector3(element.point.x, element.point.y, element.point.z)
      : null;
    this.children = element.children ? element.children.map((o) => o.clone()) : null;
  }
  getCategoryColor() {
    if (this.category > 0 && store.state.categories) {
      let category = store.state.categories.find((c) => c.id == this.category);

      if (category) {
        return {
          r: parseInt(category.color.substr(1, 2), 16) / 255,
          g: parseInt(category.color.substr(3, 2), 16) / 255,
          b: parseInt(category.color.substr(5, 2), 16) / 255,
        };
      }
    }

    return null;
  }
  parseContent() {
    super.parseContent();

    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    this.parseComments();
    if (!this.children) this.children = [];
    this.children = this.children.map((o) => Object.assign(new ForgeObject(), o));
    this.children.forEach((o) => (o.center = Object.assign(new THREE.Vector3(), o.center)));

    // FacilityGroup support: extract metadata then filter children
    this.processChildrenForFacilityGroup();

    if (this.point) this.point = Object.assign(new THREE.Vector3(), this.point);
  }
  getWatermarkColor() {
    return TAG_COLOR_HEX.Equipment;
  }
  static getLabelColor() {
    return TAG_COLOR.Equipment;
  }
  getLabelColor() {
    return ForgeEquipment.getLabelColor();
  }
  static getLabelColorVector() {
    let color = new THREE.Color(ForgeEquipment.getLabelColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getType() {
    return 'Equipment';
  }
  getInfoComponent() {
    return 'EquipmentInfo';
  }
  getEditComponent() {
    return 'EquipmentEdit';
  }

  getTextureType() {
    return 'equipment';
  }
}
export class ForgePipeline extends ForgeBaseTag {
  constructor() {
    super();
    this.lineTier1 = '';
    this.lineTier2 = '';
    this.lineTier3 = '';
    this.lineSize = null;
    this.lineRouting = '';
    this.pipeSpecification = '';
    this.sequenceNumber = '';
    this.npd = '';
    this.from = '';
    this.to = '';
    this.pIdFromTo = '';
    this.fluidCode = '';
    this.fluidDescription = '';
    this.cove = '';
    this.insulationCode = '';
    this.insulationMm = '';
    this.tracingMaxTemperature = null;
    this.tracingMaintenanceTemperature = null;
    this.phase = '';
    this.sourService = '';
    this.operatingPressure = null;
    this.operatingTemperature = null;
    this.pmaxAtTminPressure = null;
    this.pmaxAtTminTemperature = null;
    this.pmaxAtTmaxPressure = null;
    this.pmaxAtTmaxTemperature = null;
    this.chemicalCleaning = '';
    this.processCritical = '';
    this.testConditionsPressure = null;
    this.testConditionsMedium = '';
    this.mdmt = '';
    this.testTypePressure = null;
    this.designMaxPressure = null;
    this.designMinPressure = null;
    this.designMaxTemperature = null;
    this.designMinTemperature = null;
    this.pedFluidState = '';
    this.pedFluidGroup = '';
    this.pedCategory = '';
    this.stressCriticality = '';
    this.stressCalculationNumber = '';
    this.massFlow = '';
    this.volFlowLiq = '';
    this.volFlowVap = '';
    this.weightVapor = '';
    this.pressure = '';
    this.tempInsulTemp = '';
    this.totalViscosity = '';
    this.totalDensity = '';
    this.velocity = '';
    this.deltaP = '';
    this.lethalServices = '';
    this.toxicService = '';
    this.evalWeldImperfectionFluidCategory = '';

    this.forgeAssetId = '';
    this.harvesterMatchedValue = '';
    this.point = null;
    this.tagHarvesterId = null;
    this.tagHarvester = null;
    this.children = [];
    this.isSelected = false;
  }

  getSearchProperties() {
    return [
      { property: 'lineTier1', title: 'Line - Tier 1' },
      { property: 'lineTier2', title: 'Line - Tier 2' },
      { property: 'lineTier3', title: 'Line - Tier 3' },
      { property: 'lineSize', title: 'Line Size (inches)' },
      { property: 'pipeSpecification', title: 'Pipe Specification' },
      { property: 'sequenceNumber', title: 'Sequence Number' },
      { property: 'npd', title: 'NPD (Nominal Pipe Diameter)' },
      { property: 'from', title: 'From' },
      { property: 'to', title: 'To' },
      { property: 'pIdFromTo', title: 'P&ID From/To' },
      { property: 'fluidCode', title: 'Fluid Code' },
      { property: 'fluidDescription', title: 'Fluid Description' },
      { property: 'cove', title: 'COVE' },
      { property: 'insulationCode', title: 'Insulation Code' },
      { property: 'insulationMm', title: 'Insulation Thickness (mm)' },
      { property: 'tracingMaxTemperature', title: 'Tracing - Max Temperature (°C)' },
      { property: 'tracingMaintenanceTemperature', title: 'Tracing - Maintenance Temperature (°C)' },
      { property: 'phase', title: 'Phase' },
      { property: 'sourService', title: 'Sour Service' },
      { property: 'lineRouting', title: 'Line Routing' },
      { property: 'operatingPressure', title: 'Operating - Pressure (barg)' },
      { property: 'operatingTemperature', title: 'Operating - Temperature (°C)' },
      { property: 'pmaxAtTminPressure', title: 'Pmax @ Tmin - Pressure (barg)' },
      { property: 'pmaxAtTminTemperature', title: 'Pmax @ Tmin - Temperature (°C)' },
      { property: 'pmaxAtTmaxPressure', title: 'Pmax @ Tmax - Pressure (barg)' },
      { property: 'pmaxAtTmaxTemperature', title: 'Pmax @ Tmax - Temperature (°C)' },
      { property: 'designMaxPressure', title: 'Design - Maximum Pressure (barg)' },
      { property: 'designMinPressure', title: 'Design - Minimum Pressure (barg)' },
      { property: 'designMaxTemperature', title: 'Design - Maximum Temperature (°C)' },
      { property: 'designMinTemperature', title: 'Design - Minimum Temperature (°C)' },
      { property: 'chemicalCleaning', title: 'Chemical Cleaning' },
      { property: 'processCritical', title: 'Process Critical' },
      { property: 'pedFluidState', title: 'PED - Fluid State' },
      { property: 'pedFluidGroup', title: 'PED - Fluid Group' },
      { property: 'pedCategory', title: 'PED - Fluid Category' },
      { property: 'testConditionsPressure', title: 'Test Conditions - Pressure (barg)' },
      { property: 'testConditionsMedium', title: 'Test Conditions - Medium' },
      { property: 'mdmt', title: 'MDMT (°C)' },
      { property: 'testTypePressure', title: 'Test Type - Pressure (barg)' },
      { property: 'stressCriticality', title: 'Stress - Criticality' },
      { property: 'stressCalculationNumber', title: 'Stress - Calculation Number' },
      { property: 'massFlow', title: 'Mass Flow Rated (kg/h)' },
      { property: 'volFlowLiq', title: 'Liq:Am3/h' },
      { property: 'volFlowVap', title: 'Vap:NM3/h' },
      { property: 'weightVapor', title: 'Weight % Vapor' },
      { property: 'pressure', title: 'Pressure (barg)' },
      { property: 'tempInsulTemp', title: 'Insulation Temperature (°C)' },
      { property: 'totalViscosity', title: 'Total Viscosity (cP)' },
      { property: 'totalDensity', title: 'Total Density (Kg/m3)' },
      { property: 'velocity', title: 'Velocity Rated (m/s)' },
      { property: 'deltaP', title: 'Δ P Rated (bar/Km)' },
      { property: 'lethalServices', title: 'Lethal Services' },
      { property: 'toxicService', title: 'Toxic Service' },
      { property: 'evalWeldImperfectionFluidCategory', title: 'Eval Weld Imperfection - Fluid Category' },
      { property: 'harvesterMatchedValue', title: 'Pipeline' },
      ...super.getSearchProperties(),
    ];
  }

  clone() {
    var e = new ForgePipeline();
    e.setSameParameters(this);
    return e;
  }

  setSameParameters(element) {
    super.setSameBaseParameters(element);
    this.lineTier1 = element.lineTier1;
    this.lineTier2 = element.lineTier2;
    this.lineTier3 = element.lineTier3;
    this.lineSize = element.lineSize;
    this.pipeSpecification = element.pipeSpecification;
    this.system = element.system;
    this.subSystem = element.subSystem;
    this.sequenceNumber = element.sequenceNumber;
    this.fluidCode = element.fluidCode;
    this.fluidDescription = element.fluidDescription;
    this.npd = element.npd;
    this.from = element.from;
    this.to = element.to;
    this.pIdFromTo = element.pIdFromTo;
    this.cove = element.cove;
    this.insulationCode = element.insulationCode;
    this.insulationMm = element.insulationMm;
    this.tracingMaxTemperature = element.tracingMaxTemperature;
    this.tracingMaintenanceTemperature = element.tracingMaintenanceTemperature;
    this.phase = element.phase;
    this.sourService = element.sourService;
    this.lineRouting = element.lineRouting;
    this.operatingPressure = element.operatingPressure;
    this.operatingTemperature = element.operatingTemperature;
    this.pmaxAtTminPressure = element.pmaxAtTminPressure;
    this.pmaxAtTminTemperature = element.pmaxAtTminTemperature;
    this.pmaxAtTmaxPressure = element.pmaxAtTmaxPressure;
    this.pmaxAtTmaxTemperature = element.pmaxAtTmaxTemperature;
    this.designMaxPressure = element.designMaxPressure;
    this.designMinPressure = element.designMinPressure;
    this.designMaxTemperature = element.designMaxTemperature;
    this.designMinTemperature = element.designMinTemperature;
    this.chemicalCleaning = element.chemicalCleaning;
    this.processCritical = element.processCritical;
    this.pedFluidState = element.pedFluidState;
    this.pedFluidGroup = element.pedFluidGroup;
    this.pedCategory = element.pedCategory;
    this.testConditionsPressure = element.testConditionsPressure;
    this.testConditionsMedium = element.testConditionsMedium;
    this.mdmt = element.mdmt;
    this.testTypePressure = element.testTypePressure;
    this.stressCriticality = element.stressCriticality;
    this.stressCalculationNumber = element.stressCalculationNumber;
    this.massFlow = element.massFlow;
    this.volFlowLiq = element.volFlowLiq;
    this.volFlowVap = element.volFlowVap;
    this.weightVapor = element.weightVapor;
    this.pressure = element.pressure;
    this.tempInsulTemp = element.tempInsulTemp;
    this.totalViscosity = element.totalViscosity;
    this.totalDensity = element.totalDensity;
    this.velocity = element.velocity;
    this.deltaP = element.deltaP;
    this.lethalServices = element.lethalServices;
    this.toxicService = element.toxicService;
    this.evalWeldImperfectionFluidCategory = element.evalWeldImperfectionFluidCategory;
    this.forgeAssetId = element.forgeAssetId;
    this.point = element.point
      ? new THREE.Vector3(element.point.x, element.point.y, element.point.z)
      : null;
    this.tagHarvesterId = element.tagHarvesterId;
    this.tagHarvester = element.tagHarvester;
    this.children = element.children ? element.children.map((o) => o.clone()) : null;
    this.harvesterMatchedValue = element.harvesterMatchedValue;
  }

  static getTitleForProperty(property) {
    const propertyTitles = {
      lineTier1: 'Line - Tier 1',
      lineTier2: 'Line - Tier 2',
      lineTier3: 'Line - Tier 3',
      lineSize: 'Line Size (mm)',
      pipeSpecification: 'Pipe Specification',
      system: 'System',
      subSystem: 'Sub System',
      sequenceNumber: 'Sequence Number',
      fluidCode: 'Fluid Code',
      fluidDescription: 'Fluid Description',
      npd: 'NPD (mm)',
      from: 'From',
      to: 'To',
      pIdFromTo: 'P&ID From - To',
      cove: 'Cove',
      insulationCode: 'Insulation Code',
      insulationMm: 'Insulation (mm)',
      tracingMaxTemperature: 'Tracing - Max Temperature (°C)',
      tracingMaintenanceTemperature: 'Tracing - Maintenance Temperature (°C)',
      phase: 'Phase',
      sourService: 'Sour Service',
      lineRouting: 'Line Routing',
      operatingPressure: 'Operating Pressure (bar)',
      operatingTemperature: 'Operating Temperature (°C)',
      pmaxAtTminPressure: 'Pmax @ Tmin - Pressure (bar)',
      pmaxAtTminTemperature: 'Pmax @ Tmin - Temperature (°C)',
      pmaxAtTmaxPressure: 'Pmax @ Tmax - Pressure (bar)',
      pmaxAtTmaxTemperature: 'Pmax @ Tmax - Temperature (°C)',
      designMaxPressure: 'Design - Maximum Pressure (bar)',
      designMinPressure: 'Design - Minimum Pressure (bar)',
      designMaxTemperature: 'Design - Maximum Temperature (°C)',
      designMinTemperature: 'Design - Minimum Temperature (°C)',
      chemicalCleaning: 'Chemical Cleaning',
      processCritical: 'Process Critical',
      pedFluidState: 'PED - Fluid State',
      pedFluidGroup: 'PED - Fluid Group',
      pedCategory: 'PED - Category',
      testConditionsPressure: 'Test Conditions - Pressure (bar)',
      testConditionsMedium: 'Test Conditions - Medium',
      mdmt: 'MDMT (°C)',
      testTypePressure: 'Test Type - Pressure (bar)',
      stressCriticality: 'Stress - Criticality',
      stressCalculationNumber: 'Stress - Calculation Number',
      massFlow: 'Mass Flow (kg/h)',
      volFlowLiq: 'Vol Flow Liquid (Am³/h)',
      volFlowVap: 'Vol Flow Vapor (Nm³/h)',
      weightVapor: 'Weight Vapor (kg)',
      pressure: 'Pressure (bar)',
      tempInsulTemp: 'Temperature Insulation Temperature (°C)',
      totalViscosity: 'Total Viscosity (Pa·s)',
      totalDensity: 'Total Density (kg/m³)',
      velocity: 'Velocity (m/s)',
      deltaP: 'Delta P (bar)',
      lethalServices: 'Lethal Services',
      toxicService: 'Toxic Service',
      evalWeldImperfectionFluidCategory: 'Eval Weld Imperfection Fluid Category',
      harvesterMatchedValue: 'Pipeline'
    };

    return propertyTitles[property] || super.getTitleForProperty(property) || property;
  }


  parseContent() {
    super.parseContent();
    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    this.parseComments();
    if (!this.children) this.children = [];
    this.children = this.children.map((o) => Object.assign(new ForgeObject(), o));
    this.children.forEach((o) => (o.center = Object.assign(new THREE.Vector3(), o.center)));

    // FacilityGroup support: extract metadata then filter children
    this.processChildrenForFacilityGroup();

    if (this.point) this.point = Object.assign(new THREE.Vector3(), this.point);
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }
  getGlobalPoint() {
    return this.point;
  }

  getAllDbIds() {
    var result = [];
    if (this.children) {
      result = this.children.map((o) => o.dbId);
    }
    return result;
  }
  getAllTaggedChildren() {
    var list = [this];
    if (this.children == null) return list;
    for (var i = 0; i < this.children.length; i++) {
      var tagged = this.children[i].getAllTaggedChildren();
      list = list.concat(tagged);
    }
    return list.filter((o) => o.children == null);
  }
  getUniqueName(initialName, names) {
    var result = initialName;
    var index = 1;
    while (names.includes(result)) {
      result = initialName + '(' + index + ')';
      index++;
    }
    return result;
  }

  getWatermarkColor() {
    return TAG_COLOR_HEX.Pipeline;
  }
  static getLabelColor() {
    return TAG_COLOR.Pipeline;
  }
  getLabelColor() {
    return ForgePipeline.getLabelColor();
  }
  static getLabelColorVector() {
    let color = new THREE.Color(ForgePipeline.getLabelColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getType() {
    return 'Pipeline';
  }
  getInfoComponent() {
    return 'PipelineInfo';
  }
  getEditComponent() {
    return 'PipelineEdit';
  }

  getTextureType() {
    return 'pipeline';
  }
}
export class ForgeStructural extends ForgeBaseTag {
  constructor() {
    super();
    this.point = null;
    this.tagHarvesterId = null;
    this.children = [];

    this.type = '';
    this.description = '';
    this.spec = '';
    this.weight = '';

    this.sectionId = '';
    this.sectionDescription = '';
    this.materialType = '';
    this.sectionDimensions = '';
    this.structuralRole = '';
    this.criticalRating = '';

    this.isSelected = false;
  }

  getSearchProperties() {
    return [
      {
        property: 'type',
        title: 'Type',
      },
      {
        property: 'description',
        title: 'Description',
      },
      {
        property: 'spec',
        title: 'Spec',
      },
      {
        property: 'weight',
        title: 'Weight',
      },
      {
        property: 'sectionId',
        title: 'Section Id',
      },
      {
        property: 'sectionDescription',
        title: 'Section Description',
      },
      {
        property: 'materialType',
        title: 'Material Type',
      },
      {
        property: 'sectionDimensions',
        title: 'Section Dimensions',
      },
      {
        property: 'structuralRole',
        title: 'Structural Role',
      },
      {
        property: 'criticalRating',
        title: 'Critical Rating',
      },
      ...super.getSearchProperties(),
    ];
  }

  clone() {
    var e = new ForgeStructural();
    e.setSameParameters(this);
    return e;
  }

  setSameParameters(element) {
    this.setSameBaseParameters(element);
    this.type = element.type;
    this.description = element.description;
    this.spec = element.spec;
    this.weight = element.weight;
    this.point = element.point ? element.point.clone() : null;
    this.children = element.children ? element.children.map((o) => o.clone()) : null;
    this.tagHarvesterId = element.tagHarvesterId;
    this.sectionId = element.sectionId;
    this.sectionDescription = element.sectionDescription;
    this.materialType = element.materialType;
    this.sectionDimensions = element.sectionDimensions;
    this.structuralRole = element.structuralRole;
    this.criticalRating = element.criticalRating;
  }

  parseContent() {
    super.parseContent();
    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    this.parseComments();
    if (!this.children) this.children = [];
    this.children = this.children.map((o) => Object.assign(new ForgeObject(), o));
    this.children.forEach((o) => (o.center = Object.assign(new THREE.Vector3(), o.center)));

    // FacilityGroup support: extract metadata then filter children
    this.processChildrenForFacilityGroup();

    if (this.point) this.point = Object.assign(new THREE.Vector3(), this.point);
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }
  getGlobalPoint() {
    return this.point;
  }

  getAllDbIds() {
    var result = [];
    if (this.children) {
      result = this.children.map((o) => o.dbId);
    }
    return result;
  }
  getAllTaggedChildren() {
    var list = [this];
    if (this.children == null) return list;
    for (var i = 0; i < this.children.length; i++) {
      var tagged = this.children[i].getAllTaggedChildren();
      list = list.concat(tagged);
    }
    return list.filter((o) => o.children == null);
  }
  getUniqueName(initialName, names) {
    var result = initialName;
    var index = 1;
    while (names.includes(result)) {
      result = initialName + '(' + index + ')';
      index++;
    }
    return result;
  }

  getWatermarkColor() {
    return TAG_COLOR_HEX.Structural;
  }
  static getLabelColor() {
    return TAG_COLOR.Structural;
  }
  getLabelColor() {
    return ForgeStructural.getLabelColor();
  }
  static getLabelColorVector() {
    let color = new THREE.Color(ForgeStructural.getLabelColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getType() {
    return 'Structural';
  }
  getInfoComponent() {
    return 'StructuralInfo';
  }
  getEditComponent() {
    return 'StructuralEdit';
  }

  getTextureType() {
    return 'structural';
  }
}
export class ForgeAnnotation extends ForgeBaseTag {
  constructor() {
    super();
    this.description = '';
    this.level = '';
    this.point = null;
    this.level2D = null;
    this.category = 0;
    this.location = '';
    this.children = [];
    this.p_Id = '';
    this.isPrivate = false;
    this.priority = '';

    this.isSelected = false;
    this.isExpanded = false;
    this.selected3dDbids = null;
  }

  getSearchProperties() {
    return [
      {
        property: 'p_Id',
        title: 'P_Id',
      },
      {
        property: 'description',
        title: 'Description',
      },
      {
        property: 'priority',
        title: 'Priority',
      },
      {
        property: 'categoryTitle',
        title: 'Category',
      },
      {
        property: 'level',
        title: 'Area',
      },
      {
        property: 'tq',
        title: 'TQ',
      },
      {
        property: 'location',
        title: 'Location',
      },
      {
        property: 'query',
        title: 'TQ',
      },
      ...super.getSearchProperties(),
    ];
  }

  get categoryTitle() {
    let category = store.state.categories.find((c) => c.id == this.category);

    if (category) {
      return category.name;
    }

    return '';
  }

  getAllDbIds() {
    var result = [];
    if (this.children) {
      result = this.children.map((o) => o.dbId);
    }
    return result;
  }
  getAllTaggedChildren() {
    var list = [this];
    if (this.children == null) return list;
    for (var i = 0; i < this.children.length; i++) {
      var tagged = this.children[i].getAllTaggedChildren();
      list = list.concat(tagged);
    }
    return list.filter((o) => o.children == null);
  }
  parseContent() {
    super.parseContent();
    if (this.point) this.point = new THREE.Vector3(this.point.x, this.point.y, this.point.z);
    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    if (!this.children) this.children = [];
    this.children = this.children.map((o) => Object.assign(new ForgeObject(), o));
    this.children.forEach((o) => (o.center = Object.assign(new THREE.Vector3(), o.center)));

    this.parseComments();
  }
  clone() {
    var elem = new ForgeAnnotation();
    elem.setSameParameters(this);
    return elem;
  }
  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }
  getGlobalPoint() {
    return this.point;
  }
  getCategoryColor() {
    if (this.category > 0 && store.state.categories) {
      let category = store.state.categories.find((c) => c.id == this.category);

      if (category) {
        return {
          r: parseInt(category.color.substr(1, 2), 16) / 255,
          g: parseInt(category.color.substr(3, 2), 16) / 255,
          b: parseInt(category.color.substr(5, 2), 16) / 255,
        };
      }
    }

    return null;
  }
  setSameParameters(element) {
    this.setSameBaseParameters(element);
    this.description = element.description;
    this.level = element.level;
    this.level2D = element.level2D;
    this.category = element.category;
    this.selected3dDbids = element.selected3dDbids;
    this.point = element.point
      ? new THREE.Vector3(element.point.x, element.point.y, element.point.z)
      : new THREE.Vector3();
    this.children = element.children ? element.children.map((o) => o.clone()) : null;
    this.p_Id = element.p_Id;
    this.isPrivate = element.isPrivate;
    this.priority = element.priority;

    this.location = element.location;
    this.tq = element.tq;
  }
  getWatermarkColor() {
    return TAG_COLOR_HEX.General;
  }
  static getLabelColor(viewType) {
    return TAG_COLOR.General;
    // return viewType === '3d' ? 0x00a8b768 : 0x00a8b7;
  }
  getLabelColor() {
    return ForgeAnnotation.getLabelColor();
  }
  static getLabelColorVector(viewType) {
    let color = new THREE.Color(ForgeAnnotation.getLabelColor(viewType));
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getType() {
    return 'General';
  }
  getInfoComponent() {
    return 'AnnotationInfo';
  }
  getEditComponent() {
    return 'AnnotationEdit';
  }

  getTextureType() {
    return 'general';
  }
}
export class ForgeInspection extends ForgeBaseTag {
  static PROPERTY_TITLES = {

    friendlyId: 'ID',
    name: 'Tag No.',
    clNo1: 'Corrosion Loop No. 1',
    status: 'Status',

    // CML Information - New
    cmlNo: 'CML No',
    cmlType: 'CML Type',
    inspectionStrategy: 'Inspection Strategy / Method',
    isoReference: 'ISO Reference / Drawing Link',

    // Inspection & Condition Data - Keep/Renamed
    ispDate: 'Last Inspection Date',
    minThickness: 'Minimum Thickness',
    measuredThickness: 'Average Thickness (Aggregated)',
    cr: 'Corrosion Rate (CR)',
    rl: 'Remaining Life (RL)',
    recommendation: 'Recommendation',
    nIspDate: 'Next Inspection Date',
    offerNextInspDate: 'Next Visual/NDI Date',

    // Inspection Reports - Keep
    isReportAudited: 'Report Audited',
    recommendationDate: 'Inspection Recommendation Date',
    resultsApprovalStatus: 'Inspection Results Approval Status',

    // Legacy fields kept for backward compatibility but not shown in edit form
    tagDescription: 'Tag Description (Asset ID)',
    pltName: 'Unit',
    pltCode: 'Unit Code',
    lno: 'Line / Equipment No',
    lnoCode: 'Line No. Code',
    ctgName: 'Category',
    workOrderNo: 'Work Order No',
    macFrom: 'From',
    macTo: 'To',
    lawName: 'General Regulations',
    makDate: 'Date of Production',
    criticality: 'Criticality',
    type: 'Type',
    flwClsName: 'Flow Class',
    flwName: 'Flow Name',
    useCase: 'Status',
    el: 'End of Life',
    rt: 'Remaining Thickness',
    offerLastInspDate: 'Latest VI/NDI Inspected Year',
    failureType: 'Failure Type',
    failureDate: 'Failure Date',
    currentRisk: 'Current Risk',
    futureRisk: 'Future Risk w/o Inspections',
    futureRiskInspections: 'Future Risk w/ Inspections',
    pressure1: 'Design Pressure 1',
    pressure2: 'Design Pressure 2',
    pressure3: 'Operating Pressure 3',
    pressure4: 'Operating Pressure 4',
    temperature1: 'Design Temperature 1',
    temperature2: 'Design Temperature 2',
    temperature3: 'Operating Temperature 3',
    temperature4: 'Operating Temperature 4',
    clNo2: 'Corrosion Loop No. 2',
    fluidClassification2: 'Fluid Classification 2',
    lawName2: 'Code 2',
    lawName3: 'Code 3',
    reportNumber: 'Inspection Report Number',
    createdDate: 'Inspection Report Created Date',
    issuedDate: 'Inspection Report Issued Date',
    resultsPendingReport: 'Inspection Results Pending Report',
    resultsExcessCorrosion: 'Inspection Results Excess Corrosion',
    frbiAnalysisCreatedDate: 'FRBI Analysis Created Date',
    targetDfDate: 'Target Degradation Factor (DF) Date',
    riskMatrixRating: 'Risk Matrix Rating',
    iowExcursionOfLimits: 'IOW Excursion of Limits',
    cathodicProtectionStartDate: 'Cathodic Protection Start Date',
    cathodicProtectionEndDate: 'Cathodic Protection End Date',
    cathodicProtectionStatus: 'Cathodic Protection Status',
    protectedUnprotected: 'Protected/Un-Protected',
    equipmentStartDate: 'Equipment Start Date',
    equipmentEndDate: 'Equipment End Date',
    inspectionIsoNumber: 'ISO Reference / Drawing Link',
    inspectionIsoRevisionNumber: 'Inspection Iso Revision Number',
    p_IdDrawingNumber: 'P&ID Drawing Number',
    p_IdRevisionNumber: 'P&ID Revision Number',
    severity: 'Severity',
    observation: 'Observation',
    // Additional entries for API key coverage
    assetClass: 'Asset Class',
    macType: 'Item Type',
    mkrName: 'Manufacturer',
    fncName: 'Function',
    pressureUnit: 'Pressure Unit Type',
    temperatureUnit: 'Temperature Unit Type',
    fluidClassification1: 'Fluid Classification 1',
    lawName1: 'Code 1',
    linkedData: 'Linked Data',
    impName: 'Impact Name',
    stName: 'Standard Name',
    masterType: 'Master Type',
    clName: 'Corrosion Loop (CL No.)',
  };

  static DATE_FIELDS = [
    'el', 'ispDate', 'makDate', 'nIspDate', 'offerNextInspDate',
    'offerLastInspDate', 'failureDate', 'createdDate', 'issuedDate',
    'recommendationDate', 'frbiAnalysisCreatedDate', 'targetDfDate',
    'cathodicProtectionStartDate', 'cathodicProtectionEndDate',
    'equipmentStartDate', 'equipmentEndDate'
  ];

  constructor() {
    super();

    // Initialize properties
    this.friendlyId = null;
    this.status = null;
    this.tagDescription = null;
    this.pltName = null;
    this.pltCode = null;
    this.lno = null;
    this.lnoCode = null;
    this.ctgName = null;
    this.workOrderNo = null;
    this.clName = null;
    this.macFrom = null;
    this.macTo = null;
    this.lawName = null;
    this.makDate = null;
    this.criticality = null;
    this.type = null;
    this.flwClsName = null;
    this.flwName = null;
    this.useCase = null;

    // CML Information
    this.cmlNo = null;
    this.cmlType = null;
    this.inspectionStrategy = null;
    this.isoReference = null;

    // Inspection & Condition Data
    this.ispDate = null;
    this.cr = null;
    this.rl = null;
    this.el = null;
    this.minThickness = null;
    this.measuredThickness = null;
    this.rt = null;
    this.nIspDate = null;
    this.offerNextInspDate = null;
    this.offerLastInspDate = null;
    this.failureType = null;
    this.failureDate = null;
    this.currentRisk = null;
    this.futureRisk = null;
    this.futureRiskInspections = null;
    this.pressure1 = null;
    this.pressure2 = null;
    this.pressure3 = null;
    this.pressure4 = null;
    this.temperature1 = null;
    this.temperature2 = null;
    this.temperature3 = null;
    this.temperature4 = null;
    this.clNo1 = null;
    this.clNo2 = null;
    this.fluidClassification2 = null;
    this.lawName2 = null;
    this.lawName3 = null;
    this.reportNumber = null;
    this.createdDate = null;
    this.issuedDate = null;
    this.isReportAudited = null;
    this.recommendationDate = null;
    this.resultsApprovalStatus = null;
    this.resultsPendingReport = null;
    this.resultsExcessCorrosion = null;
    this.frbiAnalysisCreatedDate = null;
    this.targetDfDate = null;
    this.recommendation = null;
    this.riskMatrixRating = null;
    this.iowExcursionOfLimits = null;
    this.cathodicProtectionStartDate = null;
    this.cathodicProtectionEndDate = null;
    this.cathodicProtectionStatus = null;
    this.protectedUnprotected = null;
    this.equipmentStartDate = null;
    this.equipmentEndDate = null;
    this.inspectionIsoNumber = null;
    this.inspectionIsoRevisionNumber = null;
    this.p_IdDrawingNumber = null;
    this.p_IdRevisionNumber = null;
    this.severity = null;

    // Collection properties
    this.subTags = [];
    this.children = [];
    this.relatedCMLTags=[];
    this.cmlRecords=[];
    // State properties
    this.point = null;
    this.level2D = null;
    this.isFetched = false;
    this.selected3dDbids = null;
    this.isSelected = false;
    this.baseStyleUrl = null;
    this.thickUrl = null;
    this.masterType = null;
    this.anomalyInsightId = null;

    // Date calculations
    this.ispDateDays = null;
    this.offerLastInspDateDays = null;
    this.nearestLastDateDays = null;
    this.nIspDateDays = null;
    this.offerNextInspDateDays = null;
    this.closestNextDateDays = null;
    this.relatedCMLTags=[];
    // Visual properties
    this.pinColor = TAG_COLOR_HEX.Inspection;
    this.observation=null;
  }

  getSearchProperties() {
    const properties = Object.entries(ForgeInspection.PROPERTY_TITLES)
      .map(([property, title]) => ({ property, title }));

    return [
      ...super.getSearchProperties(),
      ...properties
    ];
  }

  static getTitleForProperty(property) {
    return ForgeInspection.PROPERTY_TITLES[property]
      || super.getTitleForProperty(property)
      || property;
  }

  get category() {
    return this.ctgName;
  }

  get categoryTitle() {
    if (this.isFetched) return this.ctgName;
    return this._getCategory()?.name || '';
  }

  getCategoryColor() {
    const category = this._getCategory();
    if (!category?.color) return null;

    const hex = category.color.substr(1);
    return {
      r: parseInt(hex.substr(0, 2), 16) / 255,
      g: parseInt(hex.substr(2, 2), 16) / 255,
      b: parseInt(hex.substr(4, 2), 16) / 255,
    };
  }

  _getCategory() {
    return store.state.categories?.find(c => c.id == this.ctgName);
  }

  getAllDbIds() {
    return this.children?.map(child => child.dbId) || [];
  }

  getAllTaggedChildren() {
    const collectTags = (node, list = []) => {
      list.push(node);
      node.children?.forEach(child => collectTags(child, list));
      return list;
    };

    return collectTags(this).filter(node => !node.children);
  }

  parseContent() {
    super.parseContent();

    // Handle vector conversion
    this.point = this._convertToVector3(this.point);
    this.subTags = this.subTags?.map(tag => ({
      ...tag,
      point: this._convertToVector3(tag.point)
    }));
    this.children = this.children?.map(child => ({
      ...child,
      center: this._convertToVector3(child.center)
    }));
    if(this.relatedCMLTags){
    var cmlTags=[];
    this.relatedCMLTags.forEach((record) => {
      let cmlTag=Object.assign(new CmlRecord(),record);
      cmlTag.parseContent();
      cmlTags.push(cmlTag);
    });
    this.relatedCMLTags=cmlTags;

    }
    // Format dates
    ForgeInspection.DATE_FIELDS.forEach(field => {
      if (this[field]) this[field] = formatDate(this[field]);
    });

    // Initialize collections
    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    this.parseComments();
    this.subTags ||= [];
    this.children ||= [];
  }

  _convertToVector3(point) {
    return point ? new THREE.Vector3(point.x, point.y, point.z) : null;
  }

  clone() {
    const clone = new ForgeInspection();
    clone.setSameParameters(this);
    clone.relatedCMLTags = structuredClone(this.relatedCMLTags);
    clone.cmlRecords = structuredClone(this.cmlRecords);
    return clone;
  }

  setSameParameters(source) {
    super.setSameBaseParameters(source);

    // Copy simple properties
    Object.keys(ForgeInspection.PROPERTY_TITLES).forEach(prop => {
      this[prop] = source[prop];
    });

    // Foreign keys (not in PROPERTY_TITLES — those are display/export fields).
    // masterTagId is required by the Monitoring tab cascade (Wave 005) to resolve
    // which Master Tag owns this inspection's CMLs/TMLs; masterTagIds preserves
    // forward-compat with the deferred 1:N junction migration.
    this.anomalyInsightId = source.anomalyInsightId;
    this.masterTagId = source.masterTagId;
    this.masterTagIds = source.masterTagIds;

    // Copy complex properties
    this.subTags = source.subTags?.map(tag => ({ ...tag })) || [];
    this.children = source.children?.map(child => ({ ...child })) || [];
    this.point = this._convertToVector3(source.point);

    // Copy other properties
    this.selected3dDbids = source.selected3dDbids;
    this.pinColor = TAG_COLOR_HEX.Inspection;
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }

  getGlobalPoint() {
    return this.point;
  }

  getWatermarkColor() {
    return TAG_COLOR_HEX.Inspection;
  }
  static getLabelColor() {
    return TAG_COLOR.Inspection;
  }
  getLabelColor() {
    return ForgeInspection.getLabelColor();
  }
  getPinVector() {
    let color = new THREE.Color(this.pinColor);
    return new THREE.Vector3(color.r, color.g, color.b);
  }
  static getLabelColorVector() {
    let color = new THREE.Color(ForgeInspection.getLabelColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getType() {
    return 'Inspection';
  }
  getInfoComponent() {
    return 'InspectionInfo';
  }
  getEditComponent() {
    return 'InspectionEdit';
  }

  getTextureType() {
    return (this.severity && store.getters.IS_AI_INSPECTION_PROPERTY_SELECTED) ? `severity${this.severity}` : 'inspection';
  }
}

export class CmlRecord {
  static PROPERTY_TITLES = {
    name: 'Name',
    cmlNo: 'CML No',
    cmlType: 'CML Type',
    inspectionStrategy: 'Inspection Strategy',
    isoReference: 'ISO Reference',
    designThickness: 'Design Thickness',
    minimumThickness: 'Minimum Thickness',
    averageThickness: 'Average Thickness',
    remainingThickness: 'Remaining Thickness',
    corrosionRate: 'Corrosion Rate',
    remainingLife: 'Remaining Life',
    conditionRating: 'Condition Assessment',
    lastInspectionDate: 'Last Inspection Date',
    nextInspectionDate: 'Next Inspection Date',
    observation: 'Notes',
    recommendation: 'Recommendation',
    followUpDate: 'Follow Up Date',
  };

  static DATE_FIELDS = ['lastInspectionDate', 'nextInspectionDate', 'followUpDate'];

  constructor() {
    // Foreign key
    this.forgeInspectionId = null;

    // Basic info
    this.name = null;
    this.point = null;

    // CML Information
    this.cmlNo = null;
    this.cmlType = null;
    this.inspectionStrategy = null;
    this.isoReference = null;

    // Thickness data
    this.designThickness = null;
    this.minimumThickness = null;
    this.averageThickness = null;
    this.remainingThickness = null;

    // Condition metrics
    this.corrosionRate = null;
    this.remainingLife = null;
    this.conditionRating = null;

    // Dates
    this.lastInspectionDate = null;
    this.nextInspectionDate = null;
    this.followUpDate = null;

    // Notes and recommendations
    this.observation = null;
    this.recommendation = null;
   this.pinColor = TAG_COLOR_HEX.CmlRecord;
  }

  parseContent() {
    // Handle vector conversion for point
    this.point = this._convertToVector3(this.point);

    // Format dates
    CmlRecord.DATE_FIELDS.forEach(field => {
      if (this[field]) this[field] = formatDate(this[field]);
    });
  }

  _convertToVector3(point) {
    return point ? new THREE.Vector3(point.x, point.y, point.z) : null;
  }

  getGlobalPoint() {
    return this.point;
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }

  clone() {
    const clone = new CmlRecord();
    clone.setSameParameters(this);
    return clone;
  }

  setSameParameters(source) {
    // Copy all properties from PROPERTY_TITLES
    Object.keys(CmlRecord.PROPERTY_TITLES).forEach(prop => {
      this[prop] = source[prop];
    });

    // Copy additional properties
    this.forgeInspectionId = source.forgeInspectionId;
    this.point = this._convertToVector3(source.point);
  }

  static getTitleForProperty(property) {
    return CmlRecord.PROPERTY_TITLES[property] || property;
  }

  getSearchProperties() {
    return Object.entries(CmlRecord.PROPERTY_TITLES)
      .map(([property, title]) => ({ property, title }));
  }
}
export class ForgeInspectionSubTag {
  constructor() {
    this.id;
    this.name = '';
    this.point = null;
  }
  clone() {
    var elem = new ForgeInspectionSubTag();
    elem.setSameParameters(this);
    return elem;
  }
  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }
  getGlobalPoint() {
    return this.point;
  }
  setSameParameters(element) {
    this.id = element.id;
    this.name = element.name;
    this.point = element.point
      ? new THREE.Vector3(element.point.x, element.point.y, element.point.z)
      : new THREE.Vector3();
  }
}
/**
 * Whether a scaffold-family tag has a real anchor.
 *
 * Null and origin both mean "unplaced": a register-created tag starts at (0,0,0) because the
 * constructor materializes a Vector3, and a cleared one comes back the same way (`parseContent`
 * turns a null position into a zero vector). Neither should be drawn - a marker at the model
 * origin is the visible symptom (AVA-492).
 *
 * A free function, not only a method: the renderers also receive list projections and clones that
 * never went through the class, and a missing method would read as "unplaced" and silently hide a
 * tag that is placed.
 */
export function isScaffoldPlaced(tag) {
  return isScaffoldMarkerPlaced(tag);
}

export class ForgeScaffold extends ForgeBaseTag {
  constructor() {
    super();
    this.location = '';
    this.state = '';
    this.scale = new THREE.Vector3();
    this.position = new THREE.Vector3();
    this.max = new THREE.Vector3();
    this.type = 'scaffold'; //scaffold || permit || container
    this.status = '';
    this.workpackNumber = '';
    this.description = '';

    this.isSelected = false;
  }

  getSearchProperties() {
    return [
      ...super.getSearchProperties(),
      {
        property: 'state',
        title: 'State',
      },
      {
        property: 'status',
        title: 'Status',
      },
      {
        property: 'workpackNumber',
        title: 'Workpack No.',
      },
      {
        property: 'description',
        title: 'Description',
      },
      {
        property: 'location',
        title: 'Location',
      },
      {
        property: 'masterType',
        title: 'Type',
      },
    ];
  }

  get length() {
    return Number.parseFloat((this.max.y * this.scale.y).toFixed(2));
  }
  set length(value) {
    this.max.y = Number.parseFloat(value) / this.scale.y;
  }
  get height() {
    return Number.parseFloat((this.max.z * this.scale.z).toFixed(2));
  }
  set height(value) {
    this.max.z = Number.parseFloat(value) / this.scale.z;
  }

  get width() {
    return Number.parseFloat((this.max.x * this.scale.x).toFixed(2));
  }
  set width(value) {
    this.max.x = Number.parseFloat(value) / this.scale.x;
  }

  get viewPosition() {
    if (!store.state.viewer || !store.state.viewer.model) return null;
    return store.state.forgeService.worldToView3d(this.position, this.position);
  }
  set viewPosition(value) {
    if (!store.state.viewer || !store.state.viewer.model) return;
    this.position = store.state.forgeService.view3dToWorld(value);
  }
  getBox() {
    let position = this.viewPosition;
    var viewX = store.state.forgeService.worldToView3dLength(this.max.x * this.scale.x);
    var viewY = store.state.forgeService.worldToView3dLength(this.max.y * this.scale.y);
    var viewZ = store.state.forgeService.worldToView3dLength(this.max.z * this.scale.z);
    let min = new THREE.Vector3(
      position.x - 0.5 * viewX,
      position.y - 0.5 * viewY,
      position.z - 0.5 * viewZ,
    );
    let max = new THREE.Vector3(
      position.x + 0.5 * viewX,
      position.y + 0.5 * viewY,
      position.z + 0.5 * viewZ,
    );
    return new THREE.Box3(min, max);
  }
  getPointInView() {
    if (!this.position || (!store.state.viewer?.model && !store.state.unitScale))
      return this.position;

    return store.state.forgeService.worldToView3d(this.position, this.position);
  }
  getGlobalPoint() {
    return this.position;
  }
  getForgeColor() {
    let index = store.state.forgeService.scaffoldStates.findIndex((o) => o.state == this.state);
    if (index == 0) return '#92D050';
    else if (index == 1) return '#ffa500';
    else if (index == 2) return '#ff0000';
    return this.getWatermarkColor();
  }
  clone() {
    let copy = new ForgeScaffold();
    copy.setSameBaseParameters(this);
    copy.location = this.location;
    copy.state = this.state;
    copy.scale = this.scale.clone();
    copy.position = this.position.clone();
    copy.max = this.max.clone();
    copy.status = this.status;
    copy.workpackNumber = this.workpackNumber;
    copy.description = this.description;
    copy.type = this.type;
    return copy;
  }

  get point() {
    return new THREE.Vector3(this.position.x, this.position.y, this.position.z);
  }

  set point(value) {
    if (!value) return;

    this.position = new THREE.Vector3(value.x, value.y, value.z);
  }

  setScale(x, y, z) {
    this.scale = new THREE.Vector3(x, y, z);
  }

  setPosition(x, y, z) {
    this.viewPosition = new THREE.Vector3(x, y, z);
  }

  parseContent() {
    super.parseContent();
    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    this.parseComments();


    this.position = Object.assign(new THREE.Vector3(), this.position);
    this.max = Object.assign(new THREE.Vector3(), this.max);
    this.scale = Object.assign(new THREE.Vector3(), this.scale);
  }

  setMax(x, y, z) {
    this.max = new THREE.Vector3(x, y, z);
  }
  getWatermarkColor() {
    if (this.isScaffold()) return TAG_COLOR_HEX.Scaffold;
    else if (this.isPermit()) return TAG_COLOR_HEX.Permit;
    else if (this.isContainer()) return TAG_COLOR_HEX.Container;
  }
  getPinVector() {
    let color = new THREE.Color(this.getForgeColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }
  static getLabelColor(type, viewType) {
    if (type == 'scaffold') return TAG_COLOR.Scaffold;
    else if (type == 'permit') return TAG_COLOR.Permit;
    else if (type == 'container') return TAG_COLOR.Container;
    // if (type == 'scaffold') return viewType == '3d' ? 0xc66408 : 0xffb548;
    // else if (type == 'permit') return viewType == '3d' ? 0xff5211 : 0xf08a2b;
    // else if (type == 'container') return viewType == '3d' ? 0x39122b : 0x6d2251;
  }
  getLabelColor() {
    return ForgeScaffold.getLabelColor(this.type);
  }
  static getLabelColorVector(type, viewType) {
    let color = new THREE.Color(ForgeScaffold.getLabelColor(type, viewType));
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  isScaffold() {
    return this.type == 'scaffold';
  }
  isPermit() {
    return this.type == 'permit';
  }
  isContainer() {
    return this.type == 'container';
  }
  /** @see isScaffoldPlaced - null and origin both mean "never placed" or "cleared". */
  isPlaced() {
    return isScaffoldPlaced(this);
  }
  getFriendlyTypeName() {
    if (!this.type) return '';
    return this.type.charAt(0).toUpperCase() + this.type.slice(1);
  }

  getType() {
    return this.getFriendlyTypeName();
  }
  getInfoComponent() {
    return 'ScaffoldInfo';
  }
  getEditComponent() {
    return 'ScaffoldEdit';
  }

  getTextureType() {
    return 'general';
  }
}

export class ForgeAnomaly extends ForgeBaseTag {
  constructor() {
    super();
    this.description = '';
    this.externalLink = '';
    this.condition;
    this.componentRefId = null;
    this.componentRef = null;
    this.componentRefName = '';
  }

  getSearchProperties() {
    return [
      {
        property: 'description',
        title: 'Description',
      },
      {
        property: 'externalLink',
        title: 'External Link',
      },
      ...super.getSearchProperties(),
    ];
  }

  clone() {
    const clone = new ForgeAnomaly();
    clone.setSameParameters(this);
    return clone;
  }

  setSameParameters(source) {
    super.setSameBaseParameters(source);
    // Preserve the explicit null owner on a group-scoped Anomaly. The base constructor defaults
    // this field to the currently viewed Facility, which would otherwise create a dual-owner body.
    this.forgeAssetId = source.forgeAssetId;
    this.description = source.description;
    this.externalLink = source.externalLink;
    this.condition = source.condition;
    this.componentRefId = source.componentRefId;
    this.componentRef = source.componentRef;
    this.componentRefName = source.componentRefName || source.componentRef?.name || '';
    this.point = source.point ? new THREE.Vector3(source.point.x, source.point.y, source.point.z) : null;
    this.children = source.children ? source.children.map(child => ({ ...child })) : [];
  }

  parseContent() {
    super.parseContent();
    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    this.parseComments();
    if (!this.children) this.children = [];
    this.children = this.children.map((o) => Object.assign(new ForgeObject(), o));
    this.children.forEach((o) => (o.center = Object.assign(new THREE.Vector3(), o.center)));
    if (this.point) this.point = Object.assign(new THREE.Vector3(), this.point);
  }

  static getConditionColor(condition) {
    switch (condition) {
      case AnomalyCondition.Critical:
        return '#FF0000';
      case AnomalyCondition.Warning:
        return '#FFA500';
      case AnomalyCondition.Caution:
        return '#FFFF00';
      case AnomalyCondition.Normal:
        return '#90EE90';
      default:
        return TAG_COLOR_HEX.Anomaly;
    }
  }

  static getConditionDisplayName(condition) {
    switch (condition) {
      case AnomalyCondition.Critical:
        return 'Red';
      case AnomalyCondition.Warning:
        return 'Orange';
      case AnomalyCondition.Caution:
        return 'Yellow';
      case AnomalyCondition.Normal:
        return 'Light Green';
      default:
        return '';
    }
  }

  getConditionColor() {
    return ForgeAnomaly.getConditionColor(this.condition);
  }

  getConditionTextColor() {
    switch (this.condition) {
      case AnomalyCondition.Critical:
        return '#FFFFFF';
      case AnomalyCondition.Warning:
        return '#FFFFFF';
      case AnomalyCondition.Caution:
        return '#000000';
      case AnomalyCondition.Normal:
        return '#000000';
      default:
        return '#000000';
    }
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }

  getGlobalPoint() {
    return this.point;
  }

  getAllDbIds() {
    return this.children?.map(child => child.dbId) || [];
  }

  getWatermarkColor() {
    return this.getConditionColor();
  }

  static getLabelColor() {
    return TAG_COLOR.Anomaly;
  }

  getLabelColor() {
    return ForgeAnomaly.getLabelColor();
  }

  static getLabelColorVector() {
    let color = new THREE.Color(ForgeAnomaly.getLabelColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getPinVector() {
    let color = new THREE.Color(this.getConditionColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getType() {
    return 'Anomaly';
  }

  getInfoComponent() {
    return 'AnomalyInfo';
  }

  getEditComponent() {
    return 'AnomalyEdit';
  }

  getTextureType() {
    return 'anomaly';
  }
}

export class ForgeAvaInspect extends ForgeBaseTag {
  constructor() {
    super();

    // Auto-generated ID (format: PREFIX-FACILITY-NNNN)

    // Core Fields (KR-03)
    this.title = '';
    this.description = '';
    this.defectType = null; // Coating | Corrosion | Fabric Maintenance | Thickness | Other

    // Component / Asset Reference
    this.componentAssetRef = null;
    this.masterTagId = null;
    this.masterTag = null;

    // Zone Link & Crop Context (KR-05)
    this.zoneLinkId = null;
    // cropVolumeJson is inherited from ForgeBaseTag

    // Evidence & Anchors
    this.evidenceUrl = null;
    this.annotationUrl = null;
    this.heatMapUrl = null; // Corrosion heatmap snapshot from Potree viewer
    // point is inherited from ForgeBaseTag

    // Status & Priority
    this.status = 'Open'; // Open | In Review | Verified | Closed
    this.severity = null;
    this.priority = null;

    // Height Calculation (KR-03)
    this.heightFromDeck = null;
    this.deckBelowElevation = null;

    // Assignment
    this.assignedToUserId = null;
    this.assignedToUser = null;

    // Corrosion Analyzer Integration
    this.analyzerMode = null; // Scab Measurement (WT loss) | Coating Loss | Both
    this.analyzerStatus = 'Pending'; // Pending | In Progress | Complete
    this.analyzerResultsLinkId = null;
    this.analysisResultJson = null; // Complete corrosion analysis result including point cloud info

    // Metadata Storage (Persistent scan/point cloud references)
    this.metadataJson = null; // Stores LinkedScanInfo with pointCloudId, pointCloudUrl, pointCloudName

    // Auto-Increment Tracking
    this.sequenceNumber = 0;

    // NEW PROPERTIES - Remote Condition Assessment Filters
    // TODO: Backend Integration - These properties need to be returned from API
    // coatingCondition: 'ri0' | 'ri1' | 'ri2' | 'ri3' | 'ri4' | 'ri5' | null
    this.coatingCondition = null;
    // defectSeverity: 'criticalDefect' | 'priorityDefect' | 'defect' | 'minorImpairment' | 'goodCondition' | null
    this.defectSeverity = null;

    // Geometry
    this.point = null;
    this.children = [];

    // Visual
    this.pinColor = TAG_COLOR_HEX.AvaInspect;
  }

  get analysisResult() {
    return this.analysisResultJson ? JSON.parse(this.analysisResultJson) : null;
  }

  get metadata() {
    if (!this.metadataJson) return null;
    try {
      return JSON.parse(this.metadataJson);
    } catch (error) {
      debugError('[ForgeAvaInspect] Error parsing metadataJson:', error);
      return null;
    }
  }

  // Backward compatibility getter for pointCloudId
  get pointCloudId() {
    // Priority: metadataJson → analysisResultJson → null
    if (this.metadata?.pointCloudId) {
      return this.metadata.pointCloudId;
    }
    return this.analysisResult?.pointCloudData?.pointCloudId || null;
  }

  // Derived point cloud data getters - retrieve from store using pointCloudId
  get pointCloudData() {
    if (!this.pointCloudId) return null;
    return store.state.pointClouds?.find(pc => pc.id === this.pointCloudId) || null;
  }

  get pointCloudUrl() {
    return this.pointCloudData?.url || null;
  }

  get pointCloudName() {
    return this.pointCloudData?.name || null;
  }

  get analysisForgeCropBox() {
    var cropBox = this.analysisResult?.pointCloudData?.cropBox || null;
    if (cropBox) {
      return new THREE.Box3(
        new THREE.Vector3(cropBox.min.x, cropBox.min.y, cropBox.min.z),
        new THREE.Vector3(cropBox.max.x, cropBox.max.y, cropBox.max.z)
      );
    }
    return null;
  }

  // Corrosion Analysis Convenience Methods
  get maxDistance() {
    let distanceInM = this.analysisResult?.statistics?.maxDistance;
    if (distanceInM) {
      return distanceInM * 1000;
    }
    return null;
  }

  get predictedWtLoss() {
    if (!this.maxDistance) return null;
    return CORROSION_CALCULATION.calculateWTLoss(this.maxDistance);
  }

  get corrosionCategory() {
    if (!this.maxDistance) return null;
    return getCategoryByMaxDistance(this.maxDistance);
  }

  get categoryName() {
    return this.corrosionCategory?.name || null;
  }

  get categoryId() {
    return this.corrosionCategory?.id || null;
  }

  get severityLevel() {
    return this.corrosionCategory?.severityLevel || null;
  }

  get categoryColor() {
    return this.corrosionCategory?.color || null;
  }

  // These return the category THRESHOLDS (minimums), not the actual calculated values
  get categoryThresholdThicknessLoss() {
    return this.corrosionCategory?.thresholdThicknessLoss || null;
  }

  get categoryThresholdMetalLoss() {
    return this.corrosionCategory?.thresholdMetalLoss || null;
  }

  // NEW GETTERS - Coating Condition
  get coatingConditionLabel() {
    const labels = {
      'ri0': 'Ri0 (0% rusted)',
      'ri1': 'Ri1 (0.05% rusted)',
      'ri2': 'Ri2 (0.5% rusted)',
      'ri3': 'Ri3 (1% rusted)',
      'ri4': 'Ri4 (8-39% rusted)',
      'ri5': 'Ri5 (40-50% rusted)'
    };
    return labels[this.coatingCondition] || 'N/A';
  }

  get coatingConditionColor() {
    const colors = {
      'ri0': '#4CAF50',
      'ri1': '#8BC34A',
      'ri2': '#CDDC39',
      'ri3': '#FFEB3B',
      'ri4': '#FF9800',
      'ri5': '#F44336'
    };
    return colors[this.coatingCondition] || '#9E9E9E';
  }

  // NEW GETTERS - Defect Severity
  get severityLabel() {
    const labels = {
      'criticalDefect': 'Critical Defect',
      'priorityDefect': 'Priority Defect',
      'defect': 'Defect',
      'minorImpairment': 'Minor Impairment',
      'goodCondition': 'Good Condition'
    };
    return labels[this.defectSeverity] || 'N/A';
  }

  get severityColor() {
    const colors = {
      'criticalDefect': '#A80000',
      'priorityDefect': '#e27640ff',
      'defect': '#ffbc03ff',
      'minorImpairment': '#3ff0afff',
      'goodCondition': '#036b44ff'
    };
    return colors[this.defectSeverity] || '#9E9E9E';
  }

  get severityPriority() {
    const priorities = {
      'criticalDefect': 1,
      'priorityDefect': 2,
      'defect': 3,
      'minorImpairment': 4,
      'goodCondition': 5
    };
    return priorities[this.defectSeverity] || 99;
  }

  getSearchProperties() {
    return [
      'title',
      'description',
      'defectType',
      'componentAssetRef',
      'status',
      'severity',
      'priority',
      'assignedToUser.fullName',
      'assignedToUser.email',
    ];
  }

  clone() {
    const obj = new ForgeAvaInspect();
    obj.setSameBaseParameters(this);
    obj.setSameParameters(this);
    return obj;
  }

  setSameParameters(source) {
    if (!source) return;
    this.setSameBaseParameters(source);
    this.title = source.title;
    this.description = source.description;
    this.defectType = source.defectType;
    this.componentAssetRef = source.componentAssetRef;
    this.masterTagId = source.masterTagId;
    this.masterTag = source.masterTag;
    this.zoneLinkId = source.zoneLinkId;
    // cropVolumeJson is set in setSameBaseParameters
    this.evidenceUrl = source.evidenceUrl;
    this.annotationUrl = source.annotationUrl;
    this.heatMapUrl = source.heatMapUrl;
    this.status = source.status;
    this.severity = source.severity;
    this.priority = source.priority;
    this.heightFromDeck = source.heightFromDeck;
    this.deckBelowElevation = source.deckBelowElevation;
    this.assignedToUserId = source.assignedToUserId;
    this.assignedToUser = source.assignedToUser;
    this.analyzerMode = source.analyzerMode;
    this.analyzerStatus = source.analyzerStatus;
    this.analyzerResultsLinkId = source.analyzerResultsLinkId;
    this.analysisResultJson = source.analysisResultJson;
    this.metadataJson = source.metadataJson;
    this.sequenceNumber = source.sequenceNumber;
    // NEW PROPERTIES
    this.coatingCondition = source.coatingCondition;
    this.defectSeverity = source.defectSeverity;
    this.pinColor = source.pinColor;
    this.point = source.point ? new THREE.Vector3(source.point.x, source.point.y, source.point.z) : null;
    this.children = source.children ? source.children.map((o) => Object.assign(new ForgeObject(), o)) : [];
  }

  parseContent() {
    super.parseContent();
    this.attachments = ForgeAttachment.parseAttachments(this.attachments);
    this.parseComments();
    if (!this.children) this.children = [];
    this.children = this.children.map((o) => Object.assign(new ForgeObject(), o));
    this.children.forEach((o) => (o.center = Object.assign(new THREE.Vector3(), o.center)));
    if (this.point) this.point = Object.assign(new THREE.Vector3(), this.point);
  }

  getPointInView() {
    if (!this.point || (!store.state.viewer?.model && !store.state.unitScale)) return this.point;
    return store.state.forgeService.worldToView3d(this.point, this.point);
  }

  getGlobalPoint() {
    return this.point;
  }

  getAllDbIds() {
    return this.children?.map(child => child.dbId) || [];
  }

  getWatermarkColor() {
    return TAG_COLOR_HEX.AvaInspect;
  }

  static getLabelColor() {
    return TAG_COLOR.AvaInspect;
  }

  getLabelColor() {
    return ForgeAvaInspect.getLabelColor();
  }

  static getLabelColorVector() {
    let color = new THREE.Color(ForgeAvaInspect.getLabelColor());
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getPinVector() {
    let color = new THREE.Color(TAG_COLOR.AvaInspect);
    return new THREE.Vector3(color.r, color.g, color.b);
  }

  getType() {
    return 'AVA-Inspect';
  }

  getInfoComponent() {
    return 'AvaInspectInfo';
  }

  getEditComponent() {
    return 'AvaInspectEdit';
  }

  getTextureType() {
    return 'avaInspect';
  }
}

export class MappingViewersInfo {
  constructor() {
    this.min;
    this.max;
  }

  isReady() {
    if (!this.min || !this.max) return false;
    return true;
  }
  /**
   * A tag with no position has no 2D point either, and that is a normal state, not a fault: every
   * tag type can be cleared, and the handlers ask for a 2D point for whatever is in the list. The
   * point used to go straight into toPoint2D, which read `.x` off null and took the whole label
   * pass down with it - an uncaught TypeError that stopped the 2D plan drawing ANY tag, including
   * the ones that were still placed.
   *
   * Null is the answer this method already gives when it cannot map a point, so every caller
   * already handles it.
   */
  _3dTo2d(generalViewPoint) {
    if (!generalViewPoint) return null;

    if (!this.isReady()) {
      debugLog('%cMappingViewersInfo is not ready', 'color: red');
      return null;
    }

    let planBox = store.state.planViewer.model.getBoundingBox();
    let minPt2d = planBox.min;
    let maxPt2d = planBox.max;
    minPt2d.setZ(0);
    maxPt2d.setZ(0);

    //get moving distance
    var globalDist = this.min.distanceTo(this.max);
    var planDist = minPt2d.distanceTo(maxPt2d);
    var movingDistance2D = toPoint2D(this.min).distanceTo(toPoint2D(generalViewPoint));
    var movingDist = (movingDistance2D * planDist) / globalDist;

    //moving vector from point1 in plan view to result point
    var movingVec = new THREE.Vector3(
      generalViewPoint.x - this.min.x,
      generalViewPoint.y - this.min.y,
      0,
    );
    movingVec = movingVec.normalize().multiplyScalar(movingDist);
    return minPt2d.clone().add(movingVec);
  }
  _2dTo3d(planPoint) {
    let plabBox = store.state.planViewer.model.getBoundingBox();
    let minPt2d = plabBox.min;
    let maxPt2d = plabBox.max;
    maxPt2d.setZ(0);

    var globalDist = this.min.distanceTo(this.max);
    var planDist = toPoint2D(minPt2d).distanceTo(toPoint2D(maxPt2d));
    var planMovingDist = toPoint2D(minPt2d).distanceTo(toPoint2D(planPoint));
    var movingDist3D = (planMovingDist * globalDist) / planDist;

    var movingVec = new THREE.Vector3(planPoint.x - minPt2d.x, planPoint.y - minPt2d.y, 0);
    movingVec = movingVec.normalize().multiplyScalar(movingDist3D);
    return this.min.clone().add(movingVec);
  }
}
export class PointCloudModelInfo {
  constructor() {
    this.id;
    this.name = '';
    this.url = '';
    this.isOnlyURL = '';
    this.isUploaded = false;
    this.createDate = '';
    this.createdBy = '';

    this.isShown = false;
    this.isSelected = false;
  }

  get pointCloudsScan() {
    let scan = store.getters.GET_SCANS.find((o) => this.url.includes(o.id));

    if (scan) return scan;

    return store.getters.GET_SCANS.find((o) => o.name == this.name) ?? null;
  }
}
export class PointCloudDownload {
  constructor() {
    this.id;
    this.name = '';
    this.createDate = '';
    this.createdById = '';
    this.assetName = '';
    this.email = '';
    this.outputType = ''; //fls || lfm || recap
    this.downloadUrl = '';
    this.imagesList = [];

    this.isShown = false;
  }
}
export class ForgeLevel {
  constructor() {
    this.id;
    this.name = '';
    this.bottomElevation = 0;
    this.topElevation = 0;
    this.east = 0;
    this.west = 0;
    this.north = 0;
    this.south = 0;
    this.isExpanded = false;
    this.isCropped = false;
    this.dbids = '';
    this.openIn3D = true;
    this.elementsCount = 0;

    this.isSelected = false;
    this.isUploading = false;
    //level plan info
    this.planUrn = null;
    this.planImageUrl = null; //saved in db
    this.thumbnailUrl = ''; //detected automatically from pdf urn
    this.planWidth = 0;
    this.planHeight = 0;
    this.mappingViewersPointsInfo = {
      max: null,
      min: null,
    };
    this.isShowingIn3D = false;
    this.isSelectedToCrop = false;
  }
  get dbidsList() {
    return this.dbids ? this.dbids.split(',').map((o) => parseInt(o)) : [];
  }
  set dbidsList(value) {
    this.dbids = value.join(',');
  }
  getViewCropBox() {
    // min and max points of the cropBox calculated from user inputs (west, south, east, north)
    let min = store.state.forgeService.worldToView3d(
      new THREE.Vector3(this.west, this.south, this.bottomElevation),
    );

    if (!min) return null;

    let max = store.state.forgeService.worldToView3d(
      new THREE.Vector3(this.east, this.north, this.topElevation),
    );

    if (!max) return null;

    let minPoint = new THREE.Vector3(min.x, min.y, min.z);
    let maxPoint = new THREE.Vector3(max.x, max.y, max.z);

    // Check if the min and max points of the cropBox are within the model boundaries.
    // Suppose the min or max points are not within the model boundaries. In that case, we need to adjust the min and max points to the model boundaries with 10% tolerance to enhance the UI, not to use in any calculation because it is sure no element is out of the model boundaries.
    if (!!store.state.viewer && !!store.state.viewer.model) {
      let bBox = store.state.viewer.model.getBoundingBox();
      let MAX_SCALE = 5;
      let TOLERANCE = 0.1;

      let maxX = bBox.max.x;
      let maxY = bBox.max.y;
      let maxZ = bBox.max.z;
      let minX = bBox.min.x;
      let minY = bBox.min.y;
      let minZ = bBox.min.z;

      let canUpdateX = Math.abs(maxPoint.x - minPoint.x) > MAX_SCALE * Math.abs(maxX - minX);
      let canUpdateY = Math.abs(maxPoint.y - minPoint.y) > MAX_SCALE * Math.abs(maxY - minY);
      let canUpdateZ = Math.abs(maxPoint.z - minPoint.z) > MAX_SCALE * Math.abs(maxZ - minZ);

      // Check the minimum point.
      let x = minPoint.x;
      let y = minPoint.y;
      let z = minPoint.z;

      if (canUpdateX) {
        if (x < minX) x = minX - minX * TOLERANCE;
        else if (x > maxX) x = maxX + maxX * TOLERANCE;
      }

      if (canUpdateY) {
        if (y < minY) y = minY - minY * TOLERANCE;
        else if (y > maxY) y = maxY + maxY * TOLERANCE;
      }

      if (canUpdateZ) {
        if (z < minZ) z = minZ - minZ * TOLERANCE;
        else if (z > maxZ) z = maxZ + maxZ * TOLERANCE;
      }

      minPoint = new THREE.Vector3(x, y, z);

      // Check the maximum point.
      x = maxPoint.x;
      y = maxPoint.y;
      z = maxPoint.z;

      if (canUpdateX) {
        if (x < minX) x = minX - minX * TOLERANCE;
        else if (x > maxX) x = maxX + maxX * TOLERANCE;
      }

      if (canUpdateY) {
        if (y < minY) y = minY - minY * TOLERANCE;
        else if (y > maxY) y = maxY + maxY * TOLERANCE;
      }

      if (canUpdateZ) {
        if (z < minZ) z = minZ - minZ * TOLERANCE;
        else if (z > maxZ) z = maxZ + maxZ * TOLERANCE;
      }

      maxPoint = new THREE.Vector3(x, y, z);

      // Check if the level bounding box points are overlapped on any axis because it will be a rectangle, not a cube.
      // In those checks, with considering of tolerance 10%.
      // For example: if the points equals in X, then we need check the X value is bBox.max.x, or bBox.min.x
      // And if it the bBox.max.x we leave the maxPoint.x and change the bBox.min.x to bBox.min.x and versa versa.

      if (Math.abs(maxPoint.x - minPoint.x) < TOLERANCE) {
        if (Math.abs(maxPoint.x - (maxX + maxX * TOLERANCE)) < TOLERANCE) {
          minPoint.x = minX - minX * TOLERANCE;
        } else {
          maxPoint.x = maxX + maxX * TOLERANCE;
        }
      }

      if (Math.abs(maxPoint.y - minPoint.y) < TOLERANCE) {
        if (Math.abs(maxPoint.y - (maxY + maxY * TOLERANCE)) < TOLERANCE) {
          minPoint.y = minY - minY * TOLERANCE;
        } else {
          maxPoint.y = maxY + maxY * TOLERANCE;
        }
      }

      if (Math.abs(maxPoint.z - minPoint.z) < TOLERANCE) {
        if (Math.abs(maxPoint.z - (maxZ + maxZ * TOLERANCE)) < TOLERANCE) {
          minPoint.z = minZ - minZ * TOLERANCE;
        } else {
          maxPoint.z = maxZ + maxZ * TOLERANCE;
        }
      }
    }

    return new THREE.Box3(minPoint, maxPoint);
  }
  getGlobalCropBox() {
    const minPt = new THREE.Vector3(this.west, this.south, this.bottomElevation);
    const maxPt = new THREE.Vector3(this.east, this.north, this.topElevation);
    return new THREE.Box3(minPt, maxPt);
  }
  clone() {
    var copy = new ForgeLevel();
    copy.id = this.id;
    copy.name = this.name;
    copy.planUrn = this.planUrn;
    copy.planImageUrl = this.planImageUrl;
    copy.bottomElevation = this.bottomElevation;
    copy.topElevation = this.topElevation;
    copy.east = this.east;
    copy.west = this.west;
    copy.north = this.north;
    copy.south = this.south;
    copy.mappingViewersPointsInfo = this.mappingViewersPointsInfo;
    copy.dbidsList = this.dbidsList;
    copy.isShowingIn3D = this.isShowingIn3D;
    return copy;
  }
  hasAllMappingInfo() {
    if (
      !this.mappingViewersPointsInfo ||
      !this.mappingViewersPointsInfo.min ||
      !this.mappingViewersPointsInfo.max
    )
      return false;
    return true;
  }
  isPoint1Valid() {
    if (this.mappingViewersPointsInfo == null || this.mappingViewersPointsInfo.min == null)
      return false;
    return true;
  }
  isPoint2Valid() {
    if (this.mappingViewersPointsInfo == null || this.mappingViewersPointsInfo.max == null)
      return false;
    return true;
  }
  containsElevation(elevation) {
    return this.bottomElevation < elevation && this.topElevation > elevation;
  }
  static parseLevelsFromDB(levels) {
    var result = levels.map((o) => Object.assign(new ForgeLevel(), o));
    for (var i = 0; i < result.length; i++) {
      if (result[i].mappingViewersPointsInfo) {
        result[i].mappingViewersPointsInfo = Object.assign(
          new MappingViewersInfo(),
          result[i].mappingViewersPointsInfo,
        );

        if (result[i].mappingViewersPointsInfo.min)
          result[i].mappingViewersPointsInfo.min = Object.assign(
            new THREE.Vector3(),
            result[i].mappingViewersPointsInfo.min,
          );
        if (result[i].mappingViewersPointsInfo.max)
          result[i].mappingViewersPointsInfo.max = Object.assign(
            new THREE.Vector3(),
            result[i].mappingViewersPointsInfo.max,
          );
      }
    }
    store.state.forgeService.sortByName(result);
    return result;
  }
  filterImagesInside(images) {
    let cropBox = this.getGlobalCropBox();
    return images.filter((o) => cropBox.containsPoint(o.forgeCameraPosition));
  }
  isImageInsideLevel(image) {
    let cropBox = this.getGlobalCropBox();
    return cropBox.containsPoint(image.forgeCameraPosition);
  }
  fillAlignment(point1OnLevel, point1OnModel, point2OnLevel, point2OnModel) {
    let minPt2d = new THREE.Box3().setFromPoints([point1OnLevel, point2OnLevel]).min;
    let minPt3d = new THREE.Box3().setFromPoints([point1OnModel, point2OnModel]).min;

    let oldBox = store.state.planViewer.model.getBoundingBox();
    var dist3d = toPoint2D(point1OnModel).distanceTo(toPoint2D(point2OnModel));
    var dist2d = toPoint2D(point1OnLevel).distanceTo(toPoint2D(point2OnLevel));
    let distanceToMin2d = toPoint2D(oldBox.min).distanceTo(toPoint2D(minPt2d));
    let ratio2d_3d = dist3d / dist2d;
    let movingDist = distanceToMin2d * ratio2d_3d;

    let movingVec = toPoint2D(oldBox.min)
      .sub(toPoint2D(minPt2d))
      .normalize()
      .multiplyScalar(movingDist);
    let newMin = toPoint2D(minPt3d).add(movingVec);

    let box = store.state.planViewer.model.getBoundingBox();

    if (box?.center?.z) {
      newMin.setZ(box.center.z);
    } else {
      newMin.setZ(minPt3d.z);
    }

    let newWidth = (oldBox.max.x - oldBox.min.x) * ratio2d_3d;
    let newHeight = (oldBox.max.y - oldBox.min.y) * ratio2d_3d;
    let newMax = new THREE.Vector3(newMin.x + newWidth, newMin.y + newHeight, newMin.z);
    if (!this.mappingViewersPointsInfo) this.mappingViewersPointsInfo = new MappingViewersInfo();
    this.mappingViewersPointsInfo.min = newMin;
    this.mappingViewersPointsInfo.max = newMax;
  }
}

export class ForgeArea {
  constructor() {
    this.id = null;
    this.name = '';
    this.description = '';
    this.thumbnailUrl = '';
    this.boundingBox = new THREE.Box3(new THREE.Vector3(), new THREE.Vector3());
    this.facilityId = null;
  }

  clone() {
    var copy = new ForgeArea();
    copy.id = this.id;
    copy.name = this.name;
    copy.description = this.description;
    copy.boundingBox = this.boundingBox.clone();
    return copy;
  }

  parseContent() {
    if (this.boundingBox) {
      let min = new THREE.Vector3(this.boundingBox.min.x, this.boundingBox.min.y, this.boundingBox.min.z);
      let max = new THREE.Vector3(this.boundingBox.max.x, this.boundingBox.max.y, this.boundingBox.max.z);
      this.boundingBox = new THREE.Box3(min, max);
    }
  }

  get boundaryArray() {
    return [
      this.boundingBox.min.x,
      this.boundingBox.min.y,
      this.boundingBox.min.z,
      this.boundingBox.max.x,
      this.boundingBox.max.y,
      this.boundingBox.max.z,
    ];
  }

  getBoundingBox() {
    if (!this.boundingBox || !this.boundingBox.min || !this.boundingBox.max) return null;
    let min = new THREE.Vector3(this.boundingBox.min.x, this.boundingBox.min.y, this.boundingBox.min.z);
    let max = new THREE.Vector3(this.boundingBox.max.x, this.boundingBox.max.y, this.boundingBox.max.z);
    return new THREE.Box3(min, max);
  }
}

export class ForgeProject {
  constructor() {
    this.id;
    this.name = '';
    this.parentId = null;
    this.hasImages = false;
    this.isArchived = false;
    this.images = [];

    this.isVisable = true;
    this.isShowingIn3D = false;
    this.isExpanded = false;
  }
  setAllImagesVisable(isVisable) {
    this.images.forEach((o) => (o.isVisable = isVisable));
  }
  setAllImagesVisibleInViewers(isVisibleInViewers) {
    this.images.forEach((o) => (o.isVisibleInViewers = isVisibleInViewers));
  }
  static parseProjectsFromDB(projects) {
    var result = projects.map((o) => Object.assign(new ForgeProject(), o));
    for (var i = 0; i < result.length; i++) {
      // Hydrate each scan through the single DTO->Image360 path (Image360.fromDTO):
      // Vector3-coerces forgeCameraPosition/lockedForgeDirection + backfills a
      // default direction. The same hydration is reused by by-id (cold-cache)
      // lookups so cached and fetched scans are interchangeable.
      result[i].images = result[i].images.map((o) => Image360.fromDTO(o));
    }
    store.state.forgeService.sortByName(result);
    result.forEach((o) => store.state.forgeService.sortByName(o.images));
    return result;
  }

  get projectPointClouds() {
    let pc = store.state.pointClouds.find((o) => o.url.includes(this.id));

    if (pc) return pc;

    return store.state.pointClouds.find((o) => o.name == this.name) ?? null;
  }

  clone() {
    var copy = new ForgeProject();
    copy.id = this.id;
    copy.name = this.name;
    copy.parentId = this.parentId;
    copy.hasImages = this.hasImages;
    copy.isArchived = this.isArchived;
    copy.images = this.images.map((o) => o);
    return copy;
  }
}
export class Image360 {
  constructor() {
    this.id;
    this.name;
    this.width;
    this.height;
    this.isLocked;
    this.lockedPitch;
    this.lockedYaw;
    this.lockedForgeDirection;
    this.forgeCameraPosition;
    this.imageUrl;
    this.thumbnailUrl;
    this.dualloImageUrl;
    this.viewerLockedState;
    this.latLong;
    this.rotationX;
    this.rotationY;
    this.rotationZ;
    this.rotationAngle;
    this.scanType = ''; //faro || leica || gps || geoslam
    this.createDate = '';
    this.createdBy = '';

    this.isSelected = false; //checked
    this.isVisable = true; //filtered by search
    this.isShown = false;
    this.isVisibleInViewers = true;
    this.e57Url=null;
    this.lasUrl=null;
  }

  /**
   * Single source of truth for building an Image360 from a raw API DTO
   * (e.g. images360Service.getById). Mirrors the per-scan normalization
   * parseProjectsFromDB applies to cached scans: coerces forgeCameraPosition
   * and lockedForgeDirection to THREE.Vector3 and backfills a default
   * direction, so a by-id (cold-cache / Path-B) scan is interchangeable with a
   * cached one. Without it those fields stay plain {x,y,z} and crash the
   * .clone() consumers (openNearby360Panel, panoramaCoordinateTo3D) and
   * getNewYawAndPitch's toPoint2D.
   */
  static fromDTO(dto) {
    const image = Object.assign(new Image360(), dto);
    if (image.latLong) {
      if (!image.forgeCameraPosition && image.latLong.isIndianSystem)
        image.forgeCameraPosition = convertLatLongToXYZCoordinates_Indian(image.latLong);
      if (
        !image.lockedForgeDirection ||
        (image.lockedForgeDirection.x == 0 &&
          image.lockedForgeDirection.y == 0 &&
          image.lockedForgeDirection.z == 0)
      )
        image.lockedForgeDirection = getRotatedDirectionByNorthAngle();
    }
    image.forgeCameraPosition = Object.assign(new THREE.Vector3(), image.forgeCameraPosition);
    image.lockedForgeDirection = Object.assign(new THREE.Vector3(), image.lockedForgeDirection);
    if (!image.scanType) image.scanType = 'faro';
    return image;
  }



  get scanPointClouds() {
    let pc = store.state.pointClouds.find((o) => o.url.includes(this.id));

    if (pc) return pc;

    return store.state.pointClouds.find((o) => o.name == this.name) ?? null;
  }

  get thumbnailURL() {
    return this.thumbnailUrl;
    // if (!this.imageUrl) return '';
    // return this.imageUrl.replace(
    //   'Reality/images360',
    //   'Reality/images360Thumbnails',
    // );
  }
  static getDefaultLockDirection() {
    return new THREE.Vector3(-1, 0, 0);
  }
  getCameraPositionInViewer() {
    if (!this.forgeCameraPosition) {
      debugLog(`Camera position is not set for image ${this.name}`);
      return null;
    }

    if (this.forgeCameraPosition && !(this.forgeCameraPosition instanceof THREE.Vector3))
      this.forgeCameraPosition = new THREE.Vector3(this.forgeCameraPosition.x, this.forgeCameraPosition.y, this.forgeCameraPosition.z);

    return store.state.forgeService.worldToView3d(
      this.forgeCameraPosition,
      this.forgeCameraPosition,
    );
  }
  // Returns the camera position in world meters (scale-independent).
  // Use for consumers that need world coordinates (e.g., the 2D plan mapping
  // which expects world-space input to _3dTo2d). For viewer-space consumers
  // that drive navigation.setPosition(), use getCameraPositionInViewer() instead.
  getCameraWorldPosition() {
    if (!this.forgeCameraPosition) {
      debugLog(`Camera world position is not set for image ${this.name}`);
      return null;
    }
    if (!(this.forgeCameraPosition instanceof THREE.Vector3)) {
      this.forgeCameraPosition = new THREE.Vector3(
        this.forgeCameraPosition.x,
        this.forgeCameraPosition.y,
        this.forgeCameraPosition.z,
      );
    }
    return this.forgeCameraPosition.clone();
  }
  storeLockingInformation(forgeService, forgeViewer) {
    store.state.selectedImage.lockedYaw = forgeService.panoramaViewer.getYaw();
    store.state.selectedImage.lockedPitch = forgeService.panoramaViewer.getPitch();
    store.state.selectedImage.lockedForgeDirection = forgeViewer.navigation
      .getCamera()
      .getWorldDirection();
    // Store in world meters so the value survives future model re-uploads
    // with different unitScale / globalOffset. All three writers of
    // forgeCameraPosition must agree on world-meters as the canonical space.
    store.state.selectedImage.forgeCameraPosition = forgeService.view3dToWorld(
      forgeViewer.navigation.getPosition(),
    );
    store.state.selectedImage.isLocked = true;
    debugLog('Image360', 'storeLockingInformation wrote forgeCameraPosition (world meters)', {
      image: store.state.selectedImage.name,
      position: store.state.selectedImage.forgeCameraPosition.toArray(),
      unitScale: store.state.unitScale,
      globalOffset: store.state.globalOffset.toArray(),
    });
  }
  clone() {
    let copy = new Image360();
    copy.id = this.id;
    copy.name = this.name;
    copy.e57Url=this.e57;
    copy.lasUrl=this.lasUrl;
    copy.scanType = this.scanType;
    copy.isLocked = this.isLocked;
    copy.lockedPitch = this.lockedPitch;
    copy.lockedYaw = this.lockedYaw;
    copy.rotationX = this.rotationX;
    copy.rotationY = this.rotationY;
    copy.rotationZ = this.rotationZ;
    copy.rotationAngle = this.rotationAngle;
    copy.lockedForgeDirection = this.lockedForgeDirection
      ? this.lockedForgeDirection.clone()
      : this.lockedForgeDirection;
    copy.forgeCameraPosition = this.forgeCameraPosition
      ? this.forgeCameraPosition.clone()
      : this.forgeCameraPosition;
    copy.imageUrl = this.imageUrl;
    copy.thumbnailUrl = this.thumbnailUrl;
    copy.viewerLockedState = this.viewerLockedState;
    copy.latLong = this.latLong ? this.latLong.clone() : null;
    return copy;
  }
}

export class LatLongInfo {
  constructor() {
    this.lat = 0.0;
    this.long = 0.0;
    this.angle = 0.0;
    this.altitude = 0.0;
    this.isIndianSystem = false;
  }
  clone() {
    let copy = new LatLongInfo();
    copy.lat = this.lat;
    copy.long = this.long;
    copy.angle = this.angle;
    copy.altitude = this.altitude;
    copy.isIndianSystem = this.isIndianSystem;
    return copy;
  }
}

export class DocHubDocumentType {
  constructor() {
    this.id;
    this.name = '';
    this.isIssuable = false;
    this.isForge = false;
  }

  clone() {
    let copy = new DocHubDocumentType();
    copy.id = this.id;
    copy.name = this.name;
    copy.isIssuable = this.isIssuable;
    copy.isForge = this.isForge;
    return copy;
  }
}

export class DocHubDocument {
  constructor() {
    this.id = null;
    this.documentId = null;
    this.documentNumber = '';
    this.documentTitle = '';
    this.fileName = '';
    this.fileType = null;
    this.folderName = null;
    this.documentTypeId = null;
    this.documentType = '';
    this.assetId = null;
    this.assetName = null;
    /** @type {string|null} Facility group ID for cross-facility documents */
    this.facilityGroupId = null;
    /** @type {string|null} Facility group name for display */
    this.facilityGroupName = null;
    this.storageReference = null;
    this.forgeTagsIds = [];
    this.originalLinkedForgeTagsIds = [];
    this.url = '';
    this.createdBy = null;
    this.createDate = null;
    /** @type {string|null} User-typed engineering revision label of the current version */
    this.revisionNumber = null;
    this.thumbnailUrl = null;
    /** @type {Array} Embedded versions; list endpoints include only the current one */
    this.documentRevisions = [];
    /** @type {string|null} Admin-pinned version id; null when the highest version is current */
    this.currentRevisionId = null;
    this.isCurrentOverridden = false;
    this.currentVersionNumber = null;
    /** @type {number|null} Null when the endpoint did not count versions */
    this.versionCount = null;
    /** @type {string|null} Concurrency token, sent back on pin / revert */
    this.rowVersion = null;
  }

  /**
   * Returns the scope type of this document.
   * @returns {'FacilityGroup' | 'Facility'} The scope type
   */
  get scopeType() {
    return this.facilityGroupId ? 'FacilityGroup' : 'Facility';
  }

  clone() {
    let copy = new DocHubDocument();
    copy.id = this.id;
    copy.documentId = this.documentId;
    copy.documentNumber = this.documentNumber;
    copy.documentTitle = this.documentTitle;
    copy.fileName = this.fileName;
    copy.fileType = this.fileType;
    copy.folderName = this.folderName;
    copy.documentTypeId = this.documentTypeId;
    copy.documentType = this.documentType;
    copy.assetId = this.assetId;
    copy.assetName = this.assetName;
    copy.facilityGroupId = this.facilityGroupId;
    copy.facilityGroupName = this.facilityGroupName;
    copy.storageReference = this.storageReference;
    copy.forgeTagsIds = this.forgeTagsIds;
    copy.originalLinkedForgeTagsIds = this.originalLinkedForgeTagsIds;
    copy.url = this.url;
    copy.createdBy = this.createdBy;
    copy.createDate = this.createDate;
    copy.revisionNumber = this.revisionNumber;
    copy.thumbnailUrl = this.thumbnailUrl;
    copy.documentRevisions = this.documentRevisions;
    copy.currentRevisionId = this.currentRevisionId;
    copy.isCurrentOverridden = this.isCurrentOverridden;
    copy.currentVersionNumber = this.currentVersionNumber;
    copy.versionCount = this.versionCount;
    copy.rowVersion = this.rowVersion;
    return copy;
  }
}

export class ForgeAttachment {
  constructor() {
    this.id;
    this.guid = '';
    this.name = '';
    this.url = '';
    this.absoluteUrl = '';
    this.imageUrl = '';
    this.createdBy = '';
    this.createDate = '';
    this.markupsCompiledPdfAbsolutePath = '';
    this.referenceDocumentId = '';
    this.referenceContextId = '';
    this.referenceContentId = '';
    //Reality: the document version (DocumentRevision id) the markups were drawn on.
    //Keep it on re-post; when it is missing the API stamps the current version.
    this.documentRevisionId = null;
    this.isSelected = false;
    this.pagesMarkups = [];
  }
  clone() {
    var elem = new ForgeAttachment();
    elem.id = this.id;
    elem.guid = this.guid;
    elem.name = this.name;
    elem.url = this.url;
    elem.absoluteUrl = this.absoluteUrl;
    elem.imageUrl = this.imageUrl;
    elem.createdBy = this.createdBy;
    elem.createDate = this.createDate;
    elem.markupsCompiledPdfAbsolutePath = this.markupsCompiledPdfAbsolutePath;
    //The document that has attachment to it
    //Origin: DocumentFilePageID
    //Reality: DocHubDocument Id
    elem.referenceDocumentId = this.referenceDocumentId;
    //the container that has link to this attachment
    //For Origin: the id of the TestPackage/Issue
    //For Reality: the id of the Dochub, tag, annotation,… that has link to the attachment.
    elem.referenceContextId = this.referenceContextId;
    //Used as attachment content/folder in parent site
    elem.referenceContentId = this.referenceContentId;
    elem.documentRevisionId = this.documentRevisionId;
    elem.pagesMarkups = this.pagesMarkups;
    return elem;
  }
  static parseAttachments(attachments) {
    let results = [];
    if (!attachments) attachments = [];
    results = attachments.map((o) => Object.assign(new ForgeAttachment(), o));
    results.forEach((o) => {
      if (!o.pagesMarkups) o.pagesMarkups = [];
      o.pagesMarkups = o.pagesMarkups.map((e) => Object.assign(new ForgePageMarkup(), e));
      o.pagesMarkups.forEach((o) => {
        if (o.cropBox) {
          o.cropBox = Object.assign(new THREE.Box3(o.cropBox.min, o.cropBox.max));
        }

        if (o.links) {
          o.links = o.links.map((l) => Object.assign(new ForgeMarkupLink(), l));
        }
      });
    });
    return results;
  }
}
export class ForgePageMarkup {
  constructor() {
    this.id;
    this.guid;
    this.markupText = '';
    this.pageId;
    this.rotation = 0;
    this.imageUrl = '';
    this.imageAbsoluteUrl = '';
    this.svg = '';
    this.cropBox = null;
    this.links = [];
    this.deletedLinkIds = [];
    this.comments = [];
    this.imageBase64 = ''; //DTO to send image to backend
  }
  clone() {
    var elem = new ForgePageMarkup();
    elem.id = this.id;
    elem.guid = this.guid;
    elem.markupText = this.markupText;
    elem.pageId = this.pageId;
    elem.rotation = this.rotation;
    elem.imageUrl = this.imageUrl;
    elem.imageAbsoluteUrl = this.imageAbsoluteUrl;
    elem.svg = this.svg;
    elem.cropBox = this.cropBox && this.cropBox.max ? this.cropBox.clone() : null;
    elem.links = this.links && this.links.length > 0 ? this.links.map((l) => l.clone()) : null;
    elem.deletedLinkIds = [...this.deletedLinkIds];
    elem.imageBase64 = this.imageBase64;
    return elem;
  }
}
export class ForgeMarkupLink {
  constructor() {
    this.id;
    this.name = '';
    this.userId = '';
    this.createDate = store.state.forgeService.dateTimeNow();
    this.createdBy = '';
    this.linkedAttachmentId = '';
    this.forgePageMarkupId = '';
    this.position;
    this.width;
    this.height;
    this.rotation;
    this.thumbnailUrl = '';
    this.type = '';
    this.metadata = '';
  }
  clone() {
    var elem = new ForgeMarkupLink();
    elem.id = this.id;
    elem.name = this.name;
    elem.userId = this.userId;
    elem.createDate = this.createDate;
    elem.createdBy = this.createdBy;
    elem.linkedAttachmentId = this.linkedAttachmentId;
    elem.forgePageMarkupId = this.forgePageMarkupId;
    elem.position = this.position;
    elem.width = this.width ? this.width : 1;
    elem.height = this.height ? this.height : 1;
    elem.rotation = this.rotation ? this.rotation : 0;
    elem.thumbnailUrl = this.thumbnailUrl;
    elem.type = this.type;
    elem.metadata = this.metadata;
    return elem;
  }
}
export class ForgeComment {
  constructor(parentId) {
    this.id = null;
    this.text = '';
    this.userId = store?.getters['ACCOUNT/GET_USER']?.id;
    // this.createdById = store?.getters['ACCOUNT/GET_USER']?.id;
    this.createDate = store.state.forgeService.dateTimeNow();
    this.replies = [];
    this.parentId = parentId;
    this.isClosed = false;
    this.position;
    this.forgePageMarkupId = null;
    this.forgeBaseTagId = null;
    this.user = null;
  }
  clone() {
    var elem = new ForgeComment();
    elem.id = this.id;
    elem.text = this.text;
    elem.createdBy = this.createdBy;
    elem.userId = this.userId;
    elem.createDate = this.createDate;
    elem.replies = this.replies;
    elem.parentId = this.parentId;
    elem.isClosed = this.isClosed;
    elem.position = this.position;
    elem.forgePageMarkupId = this.forgePageMarkupId;
    elem.user = this.user;
    return elem;
  }
}
export class ForgeModelVersion {
  constructor() {
    this.id;
    this.name = '';
    this.urn;
    this.createdBy = '';
    this.updatedBy = '';
    this.updateDate = '';
    this.createDate = '';
    this.version = '';
    this.isMainVersion = false;
    this.isSvf2 = true;
    this.status = ModelsStatus.Active,

      this.thumbnailUrl = '';
    this.isShown = false;
    this.isSelected = false;
    this.isLoading = false;
  }
  getThumbnail(callback) {
    if (!this.urn || this.thumbnailUrl) {
      if (this.thumbnailUrl && callback) callback(this.thumbnailUrl);
      return;
    }
    var _this = this;
    getURNThumbnail(this.urn, (thumb) => {
      _this.thumbnailUrl = thumb;
      if (callback) callback(thumb);
    });
  }
}
export class ForgeCamera {
  constructor() {
    this.name = '';
    this.id;
    this.thumbnailUrl = '';
    this.viewerState = '';
    this.createDate = '';
    this.createdBy = '';
    this.folderPath = '';
    this.visibleSmallModels = '';

    this.isSelected = false;
  }

  load() {
    if (this.visibleSmallModels && this.visibleSmallModels.length > 0) {
      let hiddenModels = store.state.uploadedModels.filter((m) => !m.isShown);
      let modelsToShow = this.visibleSmallModels.split(',');
      if (modelsToShow && modelsToShow.length > 0) {
        hiddenModels.forEach((smallModel) => {
          if (modelsToShow.includes(smallModel.id.toString())) {
            store.state.forgeService.toggleShowingModel(smallModel);
          }
        });
      }
    }
    store.state.viewer.restoreState(JSON.parse(this.viewerState));
  }
}
export class ShareFileItem {
  constructor(name, id, isFolder, fileSizeBytes, url) {
    this.name = name;
    this.id = id;
    this.children = null;
    this.isFolder = isFolder;
    this.fileSize = bytesToSize(fileSizeBytes);
    this.url = url;

    this.isExpanded = false;
    this.isSelected = false;
  }
  setAllNotSelected() {
    this.isSelected = false;
    if (this.children) this.children.forEach((o) => o.setAllNotSelected());
  }
  setItemSelected(id) {
    if (this.id == id) {
      this.isSelected = true;
      return;
    }
    if (this.children) this.children.forEach((o) => o.setItemSelected(id));
  }
}
export class ForgeSettings {
  constructor() {
    this.gpsSettings = new GPSSettings();
    this.ignoredDbIds = '';
    this.modelUploadLimit = 50;
    this.isAMIS = false;
    this.inspectionFiltersColors =
      '[{"code":1,"symbol":"A","color":"#aaffff"},{"code":1,"symbol":"B","color":"#7dff7d"},{"code":1,"symbol":"C","color":"#ffff51"},{"code":1,"symbol":"D","color":"#ffa346"},{"code":1,"symbol":"E","color":"#a346ff"},{"code":1,"symbol":"F","color":"#ff0000"},{"code":1,"symbol":"N/A","color":"#0f7968"},{"code":2,"symbol":"A","color":"#ceceff"},{"code":2,"symbol":"B","color":"#adff5b"},{"code":2,"symbol":"C","color":"#ffff00"},{"code":2,"symbol":"D","color":"#ffaf60"},{"code":2,"symbol":"E","color":"#9e3eff"},{"code":2,"symbol":"F","color":"#ff0000"},{"code":2,"symbol":"N/A","color":"#0f7968"},{"code":3,"symbol":"A","color":"#ceceff"},{"code":3,"symbol":"B","color":"#acff59"},{"code":3,"symbol":"C","color":"#ffff00"},{"code":3,"symbol":"D","color":"#ffa851"},{"code":3,"symbol":"E","color":"#a244ff"},{"code":3,"symbol":"F","color":"#ff0000"},{"code":3,"symbol":"N/A","color":"#0f7968"},{"code":4,"symbol":"Over a year","color":"#65AC50"},{"code":4,"symbol":"Less than a year","color":"#FF5C0C"},{"code":4,"symbol":"Overdue","color":"#ff0000"},{"code":4,"symbol":"Next Inspection date not provided","color":"#0f7968"}]';
    this.shareFileParentId = '';
    this.isInspectionsOn = false;
    // Default Options
    this.tagsGroupingJson = '{"equipment":[],"pipeline":[],"structural":[]}'
    this.filterOptions =
      '[{"name":"Model","propertyName":"Model","isChecked":false},{"name":"Discipline","propertyName":"Discipline","isChecked":false},{"name":"Work Pack","propertyName":":WgeWorkPack","isChecked":false},{"name":"P&ID","propertyName":":WgePID","isChecked":false},{"name":"Line Number","propertyName":":WgeLINENO","isChecked":false}]';
  }

  getFilterOptionsArray() {
    if (this.filterOptions) {
      return JSON.parse(this.filterOptions).map((filter) => {
        filter.isChecked = false;
        return filter;
      });
    } else {
      return [];
    }
  }

  get tagsGrouping() {
    return this.tagsGroupingJson ? JSON.parse(this.tagsGroupingJson) : { equipment: [], pipeline: [], structural: [] };
  }

  set tagsGrouping(value) {
    debugLog(value)
    if (!value) value = { "equipment": [], "pipeline": [], "structural": [] };
    this.tagsGroupingJson = JSON.stringify(value);
  }

  getInspectionFiltersColors() {
    let result = this.inspectionFiltersColors ? JSON.parse(this.inspectionFiltersColors) : [];

    let item1 = result.find((o) => o.code == 4 && o.symbol == 'N/A');
    if (item1) item1.symbol = 'Next Inspection date not provided';

    let item2 = result.find((o) => o.code == 4 && o.symbol == 'Lessthanayear');
    if (item2) item2.symbol = 'Less than a year';

    let item3 = result.find((o) => o.code == 4 && o.symbol == 'Overayear');
    if (item3) item3.symbol = 'Over a year';

    return result;
  }
}
export class Category {
  constructor() {
    this.name = '';
    this.id = null;
    this.color = '#ff0000';
    this.assetId = '';
  }
}
export class GPSSettings {
  constructor() {
    this.isIndianSystem = false;
    this.originN = '';
    this.originE = '';
    this.x = '';
    this.y = '';
    this.angle = '';
  }
  get CONST_A() {
    let theta = Math.degToRad(this.angle);
    return Math.sin(theta) * this.x - Math.cos(theta) * this.y;
  }
  get CONST_B() {
    let theta = Math.degToRad(this.angle);
    return -Math.cos(theta) * this.x - Math.sin(theta) * this.y;
  }
}
export class UserSettings {
  constructor() {
    this.id;
    this.displaySettings = new DisplaySettings();
  }
}
export class PointCloudDisplay {
  constructor() {
    this.showPointClouds = false;
    this.colorType = 'RGB';
    this.pointSize = 3.0;
    this.pointType = 'Square';
    this.opacity = 100.0;
    this.rgbGamma = 100.0;
    this.rgbContrast = 12.0;
    this.rgbBrightness = 0.0;
     this.elevationMax = 100.0;
     this.elevationMin = 0.0;
     this.colorSchema = 'RAINBOW';
     this.pointBudget = 1e6;
     this.useEDL = false;
     this.sizeType = 'Fixed';
  }
}
export class QuickSettingsDisplay {
  constructor() {
    this.showTagsSection = true;
    this.showInspections = true;
    this.showAvaInspections = true;
    this.showOperations = true;
    this.showPipelines = true;
    this.showEquipment = true;
    this.showStructural = true;
    this.showAnomalies = true;
    this.showVisibilityRange = true;
    this.showViewSection = true;
    this.show360 = true;
    this.show3dModel = true;
    this.showPointCloud = true;
    this.showPointSize = true;
    this.showPointBudget = true;
    this.showGhostMode = true;
  }
}
export class DisplaySettings {
  constructor() {
    this.showMasterModel = true;
    this.masterModelTransparency = 100.0;
    this.renderFPS = 8;
    this.showSmallModels = false;
    this.smallModelsTransparency = 100.0;

    this.show360 = false;
    this.showInspections = false;
    this.showTags = false;
    this.showAutoEquipment = false;
    this.showStructural = false;
    this.showManualStructural = false;
    this.showPipelines = false;
    this.showManualPipelines = false;
    this.showAnnotations = false;
    this.showAnomalies = false;
    this.showAvaInspect = false;
    this.showCml = true;
    // Per-TmlPoint markers (sub-points of a CML). Default true so existing
    // CMLs with TmlPoint geometry surface their per-point markers without the
    // user having to discover a new toggle. Gated independently from showCml
    // so a power user can show CMLs but hide the TML clutter.
    this.showTml = true;
    this.showScaffolds = false;
    this.showDeckManagment = false;
    this.showPermits = false;
    this.showEquipmentPopup = true;
    this.showAnnotationPopup = true;
    this.showInspectionPopup = true;
    this.showStructuralPopup = true;
    this.showPipelinePopup = true;
    this.hideKeyboard = window.localStorage.getItem(btoa('hideKeyboard')) != 'false';
    // Doc-viewer "focus mode": dim links not belonging to the open master tag.
    // Default ON; persisted per-device (same localStorage pattern as hideKeyboard).
    this.focusMode = window.localStorage.getItem(btoa('focusMode')) !== 'false';
    this.visibilityRange = 10.0;
    // 3D viewer background. Deliberately the plain default, NOT the stored value:
    // BOTH `restoreSettings()` methods (in displaySettings.vue and
    // pointCloudsDisplayPopup.vue) work by assigning `new DisplaySettings()`, so
    // reading localStorage here would make reset re-read the old saved colour
    // instead of returning to the default. Hydration from storage happens in
    // fetchUserSettings — renderFPS is the precedent, NOT hideKeyboard, whose
    // constructor does read localStorage.
    this.viewerBackgroundColor = VIEWER_BACKGROUND_DEFAULT;

    this.displayUnits = 'm';

    this.pointCloudDisplay = new PointCloudDisplay();
    this.quickSettingsDisplay = new QuickSettingsDisplay();
  }
  getVisibilityRangeInViewer() {
    // When slider is at max (100), return infinity sentinel as-is — do NOT divide by
    // unitScale, otherwise 1000000 / 0.001 = 1,000,000,000 which still "works" numerically
    // but for metre models the sentinel is already correct (1000000m ≈ infinity).
    // The clean way: let getVisibilityRange() return the sentinel and skip the conversion.
    if (this.visibilityRange == '100') return 1000000;

    let result = this.getVisibilityRange();
    if (store.state.unitScale)
      result = result / store.state.unitScale;
    return result;
  }
  getVisibilityRange() {
    if (this.visibilityRange == '100') return 1000000; //increase it so much in case of max

    //TODO: convert here from meter to store.state.viewer units
    // let result = this.visibilityRange * 3.28084;

    return this.visibilityRange;
  }

  static get fpsLocalStorageKey() {
    return `FPS_${store.state.userId}_${store.state.assetId}`;
  }
}
export class ModelBrowserItem {
  constructor(nodeId, nodeName) {
    let instanceTree = store.state.viewer?.model?.getInstanceTree();

    this.nodeId = nodeId;
    this.nodeName = nodeName;
    this.nodeParent = undefined;
    this.nodeChildren = [];
    this.isParent = false;

    // node forge properties
    this.properties = [];
    this.details = [];
    this.isExpanding = false;
    this.isExpanded = false;
    this.isVisible = true;
    this.isIsolated = instanceTree
      ? !instanceTree.isNodeHidden(this.nodeId) && store.getters['getIsIsolating3D']
      : false;
    this.isSelected = false;
    this.isHighlighted = false;
    this.isFiltered = false;
    this.isDetailsLoaded = false;
  }

  makeAllParentVisible() {
    if (!(this.nodeParent === undefined)) {
      this.nodeParent.isExpanded = true;
      this.nodeParent.makeAllParentVisible(this.nodeParent);
    }
  }

  makeAllChildrenVisible() {
    if (this.nodeChildren && this.nodeChildren.length > 0) {
      for (let n of this.nodeChildren) {
        // n.isIsolated = false;
        n.isVisible = true;
        n.isExpanded = false;
        n.makeAllChildrenVisible();
      }
    }
  }

  makeAllChildrenIsolated() {
    if (this.nodeChildren && this.nodeChildren.length > 0) {
      for (let n of this.nodeChildren) {
        n.isIsolated = true;
        n.isVisible = true;
        // n.isExpanded = false;
        n.makeAllChildrenIsolated();
      }
    }
  }

  makeAllChildrenIsolatedValue(value) {
    if (this.nodeChildren && this.nodeChildren.length > 0) {
      for (let n of this.nodeChildren) {
        n.isIsolated = value;
        n.isVisible = true;
        n.makeAllChildrenIsolatedValue(value);
      }
    }
  }

  makeAllParentsIsolatedValue(value) {
    if (this.nodeParent) {
      this.nodeParent.isIsolated = value;
      this.nodeParent.makeAllParentsIsolatedValue(value);
    }
  }

  // Build all the node children till leafs.
  // In case the node is the root node, then it will build all the tree.
  build() {
    let tree = store.state.viewer?.model?.getInstanceTree();
    tree.enumNodeChildren(
      this.nodeId,
      (childNodeId) => {
        let childNode = new ModelBrowserItem(childNodeId, tree.getNodeName(childNodeId));

        childNode.nodeParent = this;
        childNode.isIsolated = !tree.isNodeHidden(this.nodeId) && store.getters['getIsIsolating3D'];
        childNode.isParent = tree.getChildCount(childNodeId) != 0;
        childNode.build();

        this.nodeChildren.push(childNode);

        store.dispatch('pushToAllModelItems', { value: childNode });
      },
      false,
    );
  }

  // It will build only the next level of children for the node.
  // It used to extend the parent node.
  buildOneLevel(callback) {
    debugLog(`🔎 Build One Level for Node: ${this.nodeId}`);
    this.isExpanding = true;

    let instanceTree = store.state.viewer?.model?.getInstanceTree();
    let treeIds = store.getters['getTreeIds'];
    let allModelItems = store.getters['getAllModelItems'];
    var cbCount = instanceTree.getChildCount(this.nodeId);

    instanceTree.enumNodeChildren(
      this.nodeId,
      (childNodeId) => {
        cbCount--;

        if (!allModelItems.find((n) => n.nodeId == childNodeId)) {
          if (
            treeIds.length == 0 ||
            treeIds.includes(childNodeId) ||
            childNodeId == 0 ||
            childNodeId == 1
          ) {
            let childNode = new ModelBrowserItem(
              childNodeId,
              instanceTree.getNodeName(childNodeId),
            );

            childNode.nodeParent = this;

            if (
              instanceTree.getChildCount(childNodeId) != 0 &&
              !store.getters['getLeafIds'].includes(childNodeId)
            ) {
              childNode.isParent = true;
              childNode.isIsolated = instanceTree
                ? !instanceTree.isNodeHidden(this.nodeId) && store.getters['getIsIsolating3D']
                : false;

              if (treeIds.length != 0) {
                childNode.isExpanded = true;
              }
            }

            this.nodeChildren.push(childNode);

            store.dispatch('pushToAllModelItems', { value: childNode });
          }
        }

        if (cbCount == 0) {
          // this.isExpanding = false;
          if (callback) callback();
        }
      },
      false,
    );
  }

  // It will build only the next level of children for the node.
  // It used to extend the parent node if it contains a huge mount of children.
  buildOneElementOnLevel(childNodeId) {
    let instanceTree = store.state.viewer?.model?.getInstanceTree();
    let treeIds = store.getters['getTreeIds'];

    if (
      treeIds.length == 0 ||
      treeIds.includes(childNodeId) ||
      childNodeId == 0 ||
      childNodeId == 1
    ) {
      let childNode = new ModelBrowserItem(childNodeId, instanceTree.getNodeName(childNodeId));

      childNode.nodeParent = this;

      if (
        instanceTree.getChildCount(childNodeId) != 0 &&
        !store.getters['getLeafIds'].includes(childNodeId)
      ) {
        childNode.isParent = true;
        childNode.isIsolated = instanceTree
          ? !instanceTree.isNodeHidden(this.nodeId) && store.getters['getIsIsolating3D']
          : false;

        if (treeIds.length != 0) {
          childNode.isExpanded = true;
        }
      }

      this.nodeChildren.push(childNode);

      store.dispatch('pushToAllModelItems', { value: childNode });
    }
  }

  // It will build the tree from the node point to reach all treeIds only.
  // It use with search by name in the standard mode.
  // treeIds => it is an array of filtered nodes ids.
  buildTreeFromTreeIds() {
    let instanceTree = store.state.viewer?.model?.getInstanceTree();
    let treeIds = store.getters['getTreeIds'];

    instanceTree.enumNodeChildren(
      this.nodeId,
      (childNodeId) => {
        if (treeIds.includes(childNodeId)) {
          let childNode = new ModelBrowserItem(childNodeId, instanceTree.getNodeName(childNodeId));

          childNode.nodeParent = this;

          if (instanceTree.getChildCount(childNodeId) != 0) {
            childNode.isParent = true;

            if (!store.getters['getLeafIds'].includes(childNodeId)) {
              childNode.isExpanded = true;
              childNode.isIsolated = instanceTree
                ? !instanceTree.isNodeHidden(this.nodeId) && store.getters['getIsIsolating3D']
                : false;
            } else {
              childNode.isExpanded = false;
            }
          }

          childNode.buildTreeFromTreeIds();

          this.nodeChildren.push(childNode);

          store.dispatch('pushToAllModelItems', { value: childNode });
        }
      },
      false,
    );
  }

  // Get filtered and sorted node properties.
  // IMPORTANT:: It is contains a temporary check.
  getElementProperties() {
    let ignoredProperties = [
      'child',
      'parent',
      'viewable_in',
      'source file',
      'project',
      'user',
      'last saved by',
    ];

    if (!store.state.viewer) return;

    getNodeSortedProperties(store.state.viewer, this.nodeId).then((forgeProperties) => {
      this.details = forgeProperties.reduce(function (result, { displayName, displayValue }) {
        if (!ignoredProperties.includes(displayName.toLowerCase())) {
          result.push({
            displayName,
            displayValue: displayValue !== undefined ? displayValue : '',
          });
        }
        return result;
      }, []);

      // TODO: Remove it when it is unused.
      if (store.state.assetId == '171') {
        let property = this.details.find(
          (p) => p.displayName.toLowerCase() === 'name' && p.displayValue.includes('York'),
        );
        if (property) {
          property.displayValue = property.displayValue.replace('York', '');
        }
      }
    });
  }

  // Check the leaf parents, and if there is any parent not created yet, create and build it to the leaf node.
  checkToLeaf(parents, nodeId) {
    let instanceTree = store.state.viewer?.model?.getInstanceTree();

    let children = this.nodeChildren.filter((n) => parents.includes(n.nodeId));

    let nextChild = children ? children[0] : null;

    if (nextChild) {
      parents = parents.filter((n) => n != nextChild.nodeId);

      nextChild.checkToLeaf(parents, nodeId);

      this.isExpanded = true;
      this.isParent = true;
    } else {
      if (parents.length == 0 && this.nodeId == nodeId) {
        instanceTree.enumNodeChildren(
          this.nodeId,
          (childNodeId) => {
            let childNode = new ModelBrowserItem(
              childNodeId,
              instanceTree.getNodeName(childNodeId),
            );

            childNode.isParent = false;
            childNode.isExpanded = false;
            childNode.nodeParent = this;
            childNode.build();

            this.nodeChildren.push(childNode);

            store.dispatch('pushToAllModelItems', { value: childNode });
          },
          false,
        );

        return;
      }

      let current = parents[0];

      parents = parents.filter((n) => n != current);

      let childrenCount = instanceTree.getChildCount(this.nodeId);

      if (childrenCount < 7000) {
        this.buildOneLevel();
      } else {
        this.buildOneElementOnLevel(current);
      }

      nextChild = store.getters['getAllModelItems'].filter((n) => n.nodeId == current)[0];

      if (parents.length == 0) {
        nextChild.isParent = false;
        nextChild.isExpanded = false;
      } else {
        nextChild.isParent = true;
        nextChild.isExpanded = true;
        nextChild.checkToLeaf(parents, nodeId);
      }
    }
  }
}

export class ForgeMarkupStamp {
  constructor() {
    this.id;
    this.folderPath = '';
    this.userId;
    this.createDate;
    this.createdBy = '';
    this.name = '';
    this.svgText = '';
  }
  clone() {
    var obj = new ForgeMarkupStamp();
    obj.id = this.id;
    obj.folderPath = this.folderPath;
    obj.userId = this.userId;
    obj.createDate = this.createDate;
    obj.createdBy = this.createdBy;
    obj.name = this.name;
    obj.svgText = this.svgText;
    return obj;
  }
}

export class ForgeAppearanceOptions {
  constructor() {
    this.forgeVisiblityMode = ForgeVisiblityMode.ISOLATE;
    this.selectionMode = 0; //Autodesk.Viewing.SelectionMode
    this.selectionType = 0; //Autodesk.Viewing.SelectionType
    this.colorHexOverride = '';
    this.isZoomToFit = false;
    this.clear = false;
  }
  clone() {
    var obj = new ForgeAppearanceOptions();
    obj.forgeVisiblityMode = this.forgeVisiblityMode;
    obj.selectionMode = this.selectionMode;
    obj.selectionType = this.selectionType;
    obj.colorHexOverride = this.colorHexOverride;
    obj.isZoomToFit = this.isZoomToFit;
    obj.clear = this.clear;
    return obj;
  }
  hexToVector4(hex) {
    var c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');

      return new THREE.Vector4(((c >> 16) & 255) / 255, ((c >> 8) & 255) / 255, (c & 255) / 255, 1);
    }
    throw new Error('Bad Hex');
  }
}

// Landing Page View Enum
export const LandingPageView = {
  Facility: 1,
  Client: 2,
  getDisplayName: function (value) {
    switch (value) {
      case this.Facility:
        return 'Facility View';
      case this.Client:
        return 'Client View';
      default:
        return 'Facility View';
    }
  },
  getOptions: function () {
    return [
      { value: this.Facility, text: 'Facility View' },
      { value: this.Client, text: 'Client View' }
    ];
  }
};
